// =====================================================================
// src/screens/v2/MerchantApp.jsx
//
// V2 Merchant App — business owner side of Aangan.
//
// Contains:
//   - Shell with top bar + 4-tab bottom nav
//   - Today tab (dashboard: stats, recent orders, quick actions)
//   - Catalog tab (product list + in-stock toggles)
//   - Connects tab (customers who connected with the business)
//   - Orders tab (incoming orders + accept/confirm flow)
//   - Add Product overlay (photo, name, price, stock)
//   - Order Detail overlay (accept / mark ready)
//
// In Phase B, MOCK data is replaced by Firestore queries scoped to the
// current business owner's businessId.
//
// Props:
//   onBack — exit merchant mode (return to V1 / consumer V2)
// =====================================================================

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import {
  Home, Package, MessageSquare, Settings, Plus, X,
  ChevronRight, MapPin, Phone, MoreVertical, Camera, Trash2,
  ShoppingBag, Users, TrendingUp, Bell, Eye, EyeOff, Check,
  Clock, RefreshCw, Loader2, Briefcase, Image as ImageIcon,
  Megaphone,
} from 'lucide-react';
// Phase 2.5 / C10b — Merchant wall
import WallTab from './WallTab';
import WallPostComposer from './WallPostComposer';
import { deleteWallPost } from '../../lib/v2/wallPosts';
// Phase 6.0f — trial / subscription banner on Today tab
import SubscriptionBanner from './SubscriptionBanner';
// Item #13 Phase A Step 8 — merchant notifications inbox (audience='business')
import NotificationsScreenV2 from './NotificationsScreenV2';
import {
  collection, query, where, orderBy, limit, onSnapshot,
  doc, getDoc, addDoc, setDoc, updateDoc, serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../lib/firebase';
import {
  getBusinessesByOwners,
  logoBgFromCategory,
  relativeTime,
  // C6 — openStatus derives open/closed + closing-time string from hours.
  // Used to compute the default state of the "Open today" toggle when no
  // manual override is set for today.
  openStatus,
} from '../../lib/v2/firestore';
// Phase 2.3 C2 — V2 photo upload helpers (logo + cover here; product, chat,
// FSSAI wired in later commits).
import {
  uploadBusinessLogo,
  uploadBusinessCover,
  checkBusinessLogoLimit,
  checkBusinessCoverLimit,
  bumpBusinessLogoCount,
  bumpBusinessCoverCount,
  // Phase 2.3 C3 — product image helpers
  uploadProductImage,
  deleteProductImage,
  checkProductImageQuota,
  bumpProductImageCount,
  decrementProductImageCount,
  // Phase 2.3 C3.1 — exported validators reused for product photo
  // pick (no-crop flow, validation happens at pick time).
  validateImageFile,
} from '../../lib/v2/storagePhotos';
import BusinessLogo from '../../components/BusinessLogo';
import ImageViewerModal from '../../components/ImageViewerModal';
import ManageBusinessTab from './ManageBusinessTab';
import './MerchantApp.photos.css';
import MerchantChatList from './MerchantChatList';
// Session 6c2a — Support chat opens when the merchant taps Activate/Reactivate
// on the SubscriptionBanner. Same modal + underlying chat plumbing used by
// ManageBusinessTab (Session 5a); MerchantApp adds its own instance because
// the entry point (banner on Today tab) is outside ManageBusinessTab's scope.
import SupportChatModal from './SupportChatModal';
import { openOrCreateSupportChat } from '../../lib/db';
// Session 6c2b — merchant-side inactive gating on content-modification
// surfaces (Wall compose, Catalog Add Product, TodayTab Quick Actions).
// Shared helper with Session 6c1/6c1.5/6c2a, single source of truth.
import { isBusinessActive } from '../../lib/v2/isBusinessActive';
import './MerchantApp.css';
import { buildShareUrl } from '../../lib/shareBase';

// ============================================================
// MOCK DATA
// ============================================================

// BUSINESS mock removed in Step 6.0a — top bar now uses the real
// `business` object derived from ownedBusinesses in the component body.

// TODAY_STATS mock removed in Step 6.0d — now derived from real orders
// state via useMemo. See `todayStats` in the component body.

// PRODUCTS mock removed in Step 6.0b — now subscribed from Firestore at
// /businesses/{businessId}/products/{productId}. Each product doc shape:
// { name, unit, price, inStock, icon, iconBg, createdAt, updatedAt,
//   deletedAt (null when active; serverTimestamp on soft delete) }.

// ORDERS mock removed in Step 6.0a — now loaded from Firestore. The
// real /orders docs follow the schema locked in Step 4a:
// { customerId, businessId, items[{productId,name,price,quantity}],
//   subtotal, deliveryFee, total, mode, status, displayNumber, placedAt, ... }

// CONNECTS mock removed in Step 6.0d — now subscribed from Firestore at
// /connections where businessId == activeBusinessId (rule update Step 6.0d
// added isBusinessOwner read branch). Each connection doc shape:
// { userId, businessId, status, createdAt }. Customer names/cities are
// hydrated from /users/{userId} via the customerCache effect.

// STATUS_CONFIG updated in Step 6.0a to use REAL status values that
// match the locked schema + the onOrderCreated function flow:
//
//   placed     — written by client (transient; function flips in ~2s)
//   confirmed  — function passed price validation; awaits merchant
//   preparing  — merchant started preparing
//   ready      — ready for pickup/delivery
//   completed  — delivered/picked up
//   cancelled  — cancelled by customer/merchant/function
//
// 'new' is gone (was the mock's pre-accept state; no longer exists).
// 'confirmed' is now the "action needed" UI state for merchant.
const STATUS_CONFIG = {
  placed:    { label: 'Verifying',     color: 'info',    icon: '⏳' },
  confirmed: { label: 'Action needed', color: 'accent',  icon: '⚡' },
  preparing: { label: 'Preparing',     color: 'gold',    icon: '👨‍🍳' },
  ready:     { label: 'Ready',         color: 'success', icon: '🛵' },
  completed: { label: 'Completed',     color: 'success', icon: '✓' },
  cancelled: { label: 'Cancelled',     color: 'danger',  icon: '✕' },
};

// Category → emoji for business logo (shared with Cart + OrderDetail)
const CATEGORY_EMOJI = {
  food_beverages: '🍯',
  apparel: '👗',
  services: '🔧',
  groceries: '🛒',
  electronics: '📱',
  health_beauty: '💄',
  home_decor: '🏡',
};
function emojiForCategory(cat) {
  return CATEGORY_EMOJI[cat] || '🏪';
}

// Deterministic avatar color for a customer (same UID → same color always)
const AVATAR_PALETTE = ['#C45A3F', '#3F8E5C', '#C44F8A', '#7A4FC4', '#3F6BC4', '#B8893A', '#5F8AA6'];
function avatarColorForUid(uid) {
  if (!uid) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < uid.length; i++) h = ((h << 5) - h) + uid.charCodeAt(i);
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}
function initialsFromName(name) {
  if (!name || typeof name !== 'string') return '?';
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?';
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function MerchantApp({ onBack, onOpenChat, currentUser, initialOrderId }) {
  // Stub-cleanup sweep follow-up: MerchantApp unmounts when V2App
  // navigates to order-chat (since merchant and order-chat are
  // sibling routes), so activeTab resets to 'today' on every back-
  // pop from chat. Lazy-init from sessionStorage restores the tab
  // the user was on. Pattern mirrors Path Z Commit 1's V1 ChatsList
  // tab restoration — runs synchronously during first render, no
  // race with auth/data effects.
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = sessionStorage.getItem('v2_merchant_returnTab');
      const valid = ['today', 'orders', 'chat-list', 'catalog', 'connects', 'manage'];
      if (saved && valid.includes(saved)) {
        sessionStorage.removeItem('v2_merchant_returnTab');
        return saved;
      }
    } catch (err) { /* storage disabled */ }
    return 'today';
  });
  const [overlay, setOverlay] = useState(null);   // 'add-product' | 'order' | 'edit-info' | 'edit-address' | 'wall-compose' | 'wall-edit' | null
  // Phase 2.5 / C10b — Wall composer state. `wallEditingPost` holds the
  // post object when overlay==='wall-edit' so the composer can pre-fill
  // its fields; null in create mode.
  const [wallEditingPost, setWallEditingPost] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  // C6 — isOpenToday was a local useState(true) stub that never persisted.
  // Now derived from activeBusiness.manualOpenOverride (date-stamped) with
  // a hours-based fallback. See the useMemo below `activeBusiness`.
  const [toast, setToast] = useState(null);

  // Step 6.0d: business switcher state
  // activeBusinessId tracks which of the owner's businesses is being
  // managed right now. Defaults to ownedBusinesses[0].id once Effect 1
  // resolves. Switching it re-keys the products + connections effects
  // and re-filters the orders array for Today dashboard stats.
  const [activeBusinessId, setActiveBusinessId] = useState(null);
  const [showSwitcher, setShowSwitcher] = useState(false);

  // Step 6.0d: /connections subscription (incoming connects to active biz)
  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);

  // ============================================================
  // FIRESTORE STATE (Step 6.0a) — owned businesses + live orders
  // ============================================================
  const [currentUid, setCurrentUid] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [ownedBusinesses, setOwnedBusinesses] = useState([]);
  const [loadingBusinesses, setLoadingBusinesses] = useState(true);
  const [realOrders, setRealOrders] = useState([]);  // raw Firestore docs
  const [loadingOrders, setLoadingOrders] = useState(true);
  // Customer cache: customerId -> { name, initials, color, photoUrl }
  // Avoids re-fetching the same user's profile across multiple orders.
  const [customerCache, setCustomerCache] = useState({});

  // ---- Item #13 Phase A Step 8 — merchant notifications ----
  // Subscribes to /notifications/{merchantUid}/items with audience='business'.
  // Drives the bell-badge unread count and feeds the NotificationsScreenV2
  // when the merchant taps it. The subscription lives at MerchantApp level
  // so both surfaces (badge + screen) read from the same source of truth —
  // no double-subscription, no drift.
  const [notifItems, setNotifItems] = useState([]);
  const [notifLoading, setNotifLoading] = useState(true);

  // ---- Session 6c2a — Aangan Support chat modal state ----
  // Opened by the SubscriptionBanner's Activate/Reactivate CTA on the
  // Today tab. Passes a pre-filled reactivation message that the owner
  // can edit before sending. Uses the same underlying openSupportChat
  // plumbing as ManageBusinessTab's Chat with Aangan support row
  // (Session 5a) but with its own state because the two entry points
  // (banner + Manage row) can trigger independently.
  const [supportChatId, setSupportChatId] = useState(null);
  const [supportChatPrefill, setSupportChatPrefill] = useState('');
  const [supportChatOpening, setSupportChatOpening] = useState(false);

  // ---- Effect 0: Wait for Firebase Auth to resolve ----
  // CRITICAL: on hard refresh, auth.currentUser is null for ~50-200ms
  // while Firebase restores the session from local storage. Reading
  // currentUser immediately on mount can falsely report "not signed in"
  // even when the user IS signed in. We must wait for the first auth
  // state event before deciding.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUid(user?.uid || null);
      setAuthResolved(true);
    });
    return () => unsub();
  }, []);

  // ---- Item #13 Phase A Step 8 — subscribe to business-audience notifications ----
  //
  // Lives at MerchantApp top level so the bell badge + NotificationsScreenV2
  // share a single source of truth. Only audience='business' docs are
  // surfaced here — V1's NotificationsScreen owns audience='member' + V1
  // legacy docs (no audience field).
  //
  // Real-time onSnapshot rather than one-shot fetch: badges should update
  // the moment a new connection / order lands, not on screen refresh.
  // Limit 50 mirrors V1's notification cap — keeps render cost bounded.
  useEffect(() => {
    if (!authResolved) {
      setNotifLoading(true);
      return undefined;
    }
    const uid = currentUser?.id || currentUid;
    if (!uid) {
      setNotifItems([]);
      setNotifLoading(false);
      return undefined;
    }
    const q = query(
      collection(db, 'notifications', uid, 'items'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    setNotifLoading(true);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          // Only business-audience notifs belong in MerchantApp. V1 admin
          // notifications and customer-side ('member') notifs are filtered
          // out — they're rendered by V1's NotificationsScreen.
          .filter((n) => n.audience === 'business');
        setNotifItems(items);
        setNotifLoading(false);
      },
      (err) => {
        // Permission-denied right after signOut races, etc. Treat as empty.
        console.warn('[MerchantApp] notif subscription error:', err);
        setNotifItems([]);
        setNotifLoading(false);
      }
    );
    return () => unsub();
  }, [authResolved, currentUid, currentUser?.id]);

  // ---- Item #13 Phase A Step 8 — derived unread count for the bell badge ----
  const unreadNotifCount = useMemo(
    () => notifItems.filter((n) => !n.readAt).length,
    [notifItems]
  );

  // ---- Item #13 Phase A Step 8 — notification interactions ----
  //
  // handleMarkAllNotifsRead: bulk-update every visible UNREAD doc with
  // readAt=serverTimestamp(). writeBatch keeps it to one round-trip.
  //
  // handleTapNotif: single-doc readAt update, then route via deepLink.
  // Routing is intentionally simple right now (tab-level, not order-
  // specific) — fancier "scroll to this order" can come later.
  async function handleMarkAllNotifsRead() {
    const uid = currentUser?.id || currentUid;
    if (!uid) return;
    const unread = notifItems.filter((n) => !n.readAt);
    if (unread.length === 0) return;
    try {
      const batch = writeBatch(db);
      unread.forEach((n) => {
        batch.update(
          doc(db, 'notifications', uid, 'items', n.id),
          { readAt: serverTimestamp() }
        );
      });
      await batch.commit();
      // Optimistic state update is unnecessary — onSnapshot fires
      // immediately after the writes land.
    } catch (err) {
      console.error('[MerchantApp] mark-all-read failed:', err);
      setToast('Couldn\'t mark all read — try again');
    }
  }

  async function handleTapNotif(notif) {
    const uid = currentUser?.id || currentUid;
    if (!uid || !notif?.id) return;
    // Mark read first (idempotent: re-marking is a no-op write).
    if (!notif.readAt) {
      try {
        await updateDoc(
          doc(db, 'notifications', uid, 'items', notif.id),
          { readAt: serverTimestamp() }
        );
      } catch (err) {
        console.warn('[MerchantApp] mark-read failed (non-fatal):', err);
        // Continue to routing — the user's intent was to navigate.
      }
    }
    // Route based on kind / deepLink. Phase A keeps this tab-level;
    // order-specific drilldown can come later by passing an extra
    // focus param through state.
    const screen = notif.deepLink?.screen;
    if (screen === 'merchant-orders' || notif.kind === 'new-order') {
      setActiveTab('orders');
    } else if (screen === 'merchant-connects' || notif.kind === 'new-connect') {
      setActiveTab('connects');
    } else {
      // Unknown kind — just close the screen back to Today.
      setActiveTab('today');
    }
  }

  // ---- Effect 1: Load owned businesses once auth resolves ----
  useEffect(() => {
    if (!authResolved) return undefined;
    const uid = currentUser?.id || currentUid;
    if (!uid) {
      setOwnedBusinesses([]);
      setLoadingBusinesses(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingBusinesses(true);
        const ownersMap = await getBusinessesByOwners([uid], { includeUnverified: true });
        if (cancelled) return;
        const list = ownersMap.get(uid) || [];
        setOwnedBusinesses(list);
      } catch (err) {
        console.error('[MerchantApp] owned businesses load failed:', err);
        if (!cancelled) setOwnedBusinesses([]);
      } finally {
        if (!cancelled) setLoadingBusinesses(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authResolved, currentUid, currentUser?.id]);

  // ---- Step 6.0d: initialize activeBusinessId once businesses load ----
  // Default to first owned business. Preserve the choice if it's still
  // valid after a refresh (e.g. business list shuffles). If the active id
  // no longer matches any owned business, reset to the first.
  useEffect(() => {
    if (ownedBusinesses.length === 0) {
      setActiveBusinessId(null);
      return;
    }
    const stillValid = activeBusinessId &&
      ownedBusinesses.some(b => b.id === activeBusinessId);
    if (!stillValid) {
      setActiveBusinessId(ownedBusinesses[0].id);
    }
  }, [ownedBusinesses, activeBusinessId]);

  // Derived: the currently-active business object. Used by every place
  // that previously read ownedBusinesses[0].
  const activeBusiness = useMemo(
    () => ownedBusinesses.find(b => b.id === activeBusinessId) || null,
    [ownedBusinesses, activeBusinessId]
  );

  // Session 6c2b — Derive whether the current business is active
  // (paid window OR trial window). Passed down to TodayTab / CatalogTab /
  // WallTab to gate content-modification surfaces: Quick Actions
  // Add product + New post, Catalog Add product + per-product toggle/
  // delete/replace-photo, Wall Compose post + per-post edit/delete.
  // Read-only surfaces (order details, existing customer chats, Open
  // Today toggle) deliberately stay live per Q4.
  const bizActive = isBusinessActive(activeBusiness);

  // ---- Phase 2.3 C6 — "Open today" persistence ----
  // The toggle was a local useState(true) stub. Now backed by a date-
  // stamped override field on the business doc:
  //
  //   manualOpenOverride: { date: 'YYYY-MM-DD', open: boolean }
  //
  // On load we check whether the override is for TODAY; if so we honour
  // it, otherwise we fall back to the hours-derived status from
  // openStatus(). The date stamp means stale overrides auto-expire next
  // day with no cleanup needed.
  //
  // C7.1 — Use local state (not useMemo) so the toggle can flip
  // INSTANTLY when tapped (optimistic update), without waiting for the
  // Firestore round-trip. A useEffect syncs from activeBusiness when
  // the underlying doc changes (initial load, business switch, or
  // Firestore confirming our write).
  //
  // Customer-side surfaces (BusinessProfile, BusinessDirectory, Cart,
  // ConnectList, HomeConnectListV2) still derive open/closed purely from
  // hours; propagating the override to them is a future commit. For now,
  // C6 just makes the merchant toggle persistent and self-consistent.
  const todayDateStr = useMemo(
    () => new Date().toISOString().split('T')[0],
    // Recomputed once per mount. If the merchant leaves the app open
    // overnight, the "today" string can drift — acceptable, the next
    // load will recompute correctly.
    []
  );

  // Default to true on initial mount; useEffect below corrects to the
  // real value as soon as activeBusiness loads. There's a brief tick
  // where this defaults true even if the business is actually closed,
  // but it's only the time between mount and the first sync — sub-frame
  // in practice.
  const [isOpenToday, setIsOpenToday] = useState(true);

  // Sync local state when activeBusiness's override or hours change.
  // Fires on: initial load, business switcher selection, Firestore
  // confirming an update we just wrote. Reading hours in this effect
  // means time-of-day transitions (e.g. crossing the day's closing
  // time) won't reflect until the next mount or activeBusiness change
  // — acceptable for V2.0.
  useEffect(() => {
    if (!activeBusiness) return;
    const override = activeBusiness.manualOpenOverride;
    if (override && override.date === todayDateStr) {
      setIsOpenToday(!!override.open);
    } else {
      setIsOpenToday(openStatus(activeBusiness.hours || {}).open);
    }
  }, [activeBusiness, todayDateStr]);

  // ---- Phase 2.5 / C10b — Wall post composer + delete handlers ----
  // Open composer in create mode (from "New post" Quick Action or
  // Wall tab's "Compose post" button). Both routes set the same overlay
  // state — the composer is one component, two entry points.
  function handleOpenWallCompose() {
    if (!activeBusiness) {
      setToast('No business selected');
      return;
    }
    // Session 6c2b — defense in depth. UI hides the entry points when
    // !bizActive, but if a stray call gets through we bail with a
    // helpful nudge rather than opening the composer.
    if (!bizActive) {
      setToast('Reactivate your Aangan Connect to post updates');
      return;
    }
    setWallEditingPost(null);
    setOverlay('wall-compose');
  }

  // Session 6c2b — Add product entry point defensively gated. UI hides
  // this action's buttons when !bizActive but if a stray call arrives
  // we surface the same reactivate nudge.
  function handleOpenAddProduct() {
    if (!bizActive) {
      setToast('Reactivate your Aangan Connect to add products');
      return;
    }
    setOverlay('add-product');
  }

  // Open composer in edit mode for a specific post.
  function handleEditWallPost(post) {
    if (!post) return;
    // Session 6c2b — defensive gate. WallPostCard hides its menu when
    // disabled, but any programmatic call still lands here.
    if (!bizActive) {
      setToast('Reactivate your Aangan Connect to edit posts');
      return;
    }
    setWallEditingPost(post);
    setOverlay('wall-edit');
  }

  // Delete a post. Confirm via window.confirm — V2 doesn't have a custom
  // confirm modal pattern yet, and post deletion is a meaningful action
  // worth a native confirmation. Photos + Firestore doc both deleted by
  // wallPosts.deleteWallPost.
  async function handleDeleteWallPost(post) {
    if (!post || !activeBusiness) return;
    // Session 6c2b — defensive gate.
    if (!bizActive) {
      setToast('Reactivate your Aangan Connect to delete posts');
      return;
    }
    const ok = typeof window !== 'undefined'
      ? window.confirm(`Delete this post? This can't be undone.`)
      : true;
    if (!ok) return;
    try {
      await deleteWallPost({ bizId: activeBusiness.id, postId: post.id });
      setToast('Post deleted');
    } catch (err) {
      console.error('[MerchantApp] delete wall post failed:', err);
      setToast(`Couldn't delete: ${err.code || err.message || 'try again'}`);
    }
  }

  // Close the composer (cancel or after-save). Clears editing state so
  // the next open starts fresh.
  function handleCloseWallComposer() {
    setOverlay(null);
    setWallEditingPost(null);
  }

  async function handleToggleOpenToday() {
    if (!activeBusiness) return;
    const next = !isOpenToday;
    // C7.1 — Optimistic update: flip the UI immediately so the toggle
    // feels responsive. The Firestore write happens in the background;
    // on success we leave the optimistic state as-is (the useEffect
    // above will re-sync from Firestore and confirm it). On failure we
    // revert and surface a toast.
    setIsOpenToday(next);
    try {
      await updateDoc(
        doc(db, 'businesses', activeBusiness.id),
        {
          manualOpenOverride: {
            date: todayDateStr,
            open: next,
            setAt: serverTimestamp(),
          },
          updatedAt: serverTimestamp(),
        }
      );
      setToast(next ? 'Marked open today' : 'Marked closed today');

      // Item #13 Phase A Step 6 — sidecar event for "Notify when open"
      // subscribers. Only fires when toggling ON (next === true), and
      // only AFTER the manualOpenOverride write succeeds. The event
      // doc create triggers onBusinessOpenEvent Cloud Function which
      // fans out notifications to /businesses/{id}/openSubscribers/*.
      //
      // Wrapped in its own try/catch: a sidecar failure must NOT
      // revert the optimistic toggle state, because the override
      // itself was persisted successfully. Subscribers just miss this
      // particular open event — acceptable trade-off vs. dropping the
      // merchant's authoritative state.
      if (next) {
        try {
          await addDoc(collection(db, 'businessOpenEvents'), {
            businessId: activeBusiness.id,
            setAt: serverTimestamp(),
            triggeredBy: auth.currentUser.uid,
          });
        } catch (sidecarErr) {
          console.warn('[MerchantApp] open-event sidecar write failed (override still set):', sidecarErr);
          // Don't surface to the merchant — their toggle worked.
        }
      }
    } catch (err) {
      console.error('[MerchantApp] toggle open failed:', err);
      // Revert the optimistic update — Firestore rejected the write,
      // so the merchant's underlying state hasn't actually changed.
      setIsOpenToday(!next);
      setToast(`Couldn't update: ${err.code || err.message || 'try again'}`);
    }
  }

  // ---- Phase 2.4 C8.1 — Share profile from Quick Actions ----
  // Wires the "Share profile" quick action on the merchant Today tab.
  // Uses navigator.share when available (Android/iOS native sheet) and
  // falls back to clipboard copy for desktop browsers. The share URL
  // uses the new /biz/CODE format so recipients who aren't signed in
  // hit the C8 deep-link onboarding flow (auto-connect post-signup).
  // The old ?v2=biz-profile&id=... URL works too but doesn't gracefully
  // onboard new users — share intent should always use /biz/CODE.
  // ---- Session 6c2a — Open Aangan Support chat with pre-filled message ----
  // Called from the SubscriptionBanner's Activate/Reactivate CTA. Opens
  // (or reopens — idempotent) the merchant's support chat and pre-fills
  // the composer with a reactivation message. Owner can edit before
  // sending. Admin sees the request in Support Inbox (Session 5a) and
  // can activate via ActivationModal (Session 6a).
  //
  // Deliberately keeps the SupportChatModal state at MerchantApp level
  // rather than delegating to ManageBusinessTab — the two entry points
  // (Today banner + Manage row) are on different tabs and would need
  // messy coordination to share one modal instance.
  async function handleOpenSupportChat(prefillText = '') {
    if (supportChatOpening || !currentUid) return;
    setSupportChatOpening(true);
    try {
      const { chatId } = await openOrCreateSupportChat({
        ownerUid: currentUid,
        ownerName: currentUser?.name || '',
      });
      setSupportChatPrefill(prefillText || '');
      setSupportChatId(chatId);
    } catch (err) {
      console.error('[MerchantApp] open support chat failed:', err);
      setToast('Could not open Aangan Support chat. Please try again.');
    } finally {
      setSupportChatOpening(false);
    }
  }

  // Session 6c2a — pre-filled message the SubscriptionBanner's CTA sends
  // through to the support chat modal. Editable in the composer before
  // send. Sentence case + plain-spoken per Aangan copy conventions.
  function buildReactivateMessage() {
    const bizName = activeBusiness?.name || 'my business';
    return `Hi, I'd like to reactivate Aangan Connect for ${bizName}. Please let me know how to complete the payment.`;
  }

  async function handleShareProfile() {
    if (!activeBusiness) {
      setToast('No business selected');
      return;
    }
    if (typeof window === 'undefined') return;
    // P1: must be the PUBLIC origin. window.location.origin is
    // http://localhost inside the Capacitor WebView, so a merchant sharing
    // their profile from the Android app handed out a dead link.
    const shareUrl = buildShareUrl(`/biz/${activeBusiness.id}`);
    const shareData = {
      title: activeBusiness.name || 'My business on Aangan',
      text: `Check out ${activeBusiness.name || 'my business'} on Aangan`,
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return; // native sheet handled it
      } catch (err) {
        // AbortError = user cancelled the native sheet; silently no-op.
        // Anything else falls through to clipboard.
        if (err && err.name === 'AbortError') return;
      }
    }
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setToast('Link copied to clipboard');
      } else {
        setToast(`Share link: ${shareUrl}`);
      }
    } catch (err) {
      console.error('[MerchantApp] share copy failed:', err);
      setToast(`Share link: ${shareUrl}`);
    }
  }

  // ---- Effect 2: Subscribe to orders for all owned businesses ----
  // Uses Firestore 'in' query — supports up to 30 businessIds. For
  // owners with more, we'd need to chunk; not realistic at V2.0 scale.
  useEffect(() => {
    if (loadingBusinesses) return undefined;
    if (ownedBusinesses.length === 0) {
      setRealOrders([]);
      setLoadingOrders(false);
      return undefined;
    }
    const businessIds = ownedBusinesses.map(b => b.id);
    setLoadingOrders(true);
    const q = query(
      collection(db, 'orders'),
      where('businessId', 'in', businessIds),
      orderBy('placedAt', 'desc'),
      limit(50),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setRealOrders(docs);
        setLoadingOrders(false);
      },
      (err) => {
        console.error('[MerchantApp] orders subscription failed:', err);
        setRealOrders([]);
        setLoadingOrders(false);
      }
    );
    return () => unsub();
  }, [loadingBusinesses, ownedBusinesses]);

  // ---- Effect 3: Subscribe to products of the active business (6.0b) ----
  // Active business = activeBusinessId (Step 6.0d switcher). Was previously
  // hard-coded to ownedBusinesses[0]; now re-subscribes when the merchant
  // switches between their shops.
  //
  // Soft-deleted products (deletedAt != null) are filtered client-side.
  // Firestore can't do an `!= null` query directly, and adding a synthetic
  // `isDeleted: false` field would require backfilling existing seed docs.
  // Reading every product (deleted or not) and filtering locally is cheap
  // at V2.0 scale (typical shop has tens of products, not thousands).
  //
  // No orderBy in the query: some seed docs (Step 3.6) may lack
  // `createdAt`, and Firestore's orderBy excludes docs missing the field.
  // We sort client-side instead, falling back to name when createdAt missing.
  useEffect(() => {
    if (loadingBusinesses) return undefined;
    if (!activeBusinessId) {
      setProducts([]);
      setProductsLoading(false);
      return undefined;
    }
    setProductsLoading(true);
    const q = query(collection(db, 'businesses', activeBusinessId, 'products'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => !p.deletedAt);
        docs.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? 0;
          if (ta !== tb) return tb - ta;            // newest first
          return (a.name || '').localeCompare(b.name || '');
        });
        setProducts(docs);
        setProductsLoading(false);
      },
      (err) => {
        console.error('[MerchantApp] products subscription failed:', err);
        setProducts([]);
        setProductsLoading(false);
      }
    );
    return () => unsub();
  }, [loadingBusinesses, activeBusinessId]);

  // ---- Effect 4: Subscribe to /connections of active business (6.0d) ----
  // Connection docs: { userId, businessId, status, createdAt }. We hold
  // the minimal shape in state; customer-side fields (name, city) are
  // hydrated via the existing customerCache (Effect 5).
  //
  // Rule update for 6.0d added isBusinessOwner() to the /connections read
  // rule, so this owner-side query now passes permission checks.
  useEffect(() => {
    if (loadingBusinesses) return undefined;
    if (!activeBusinessId) {
      setConnections([]);
      setConnectionsLoading(false);
      return undefined;
    }
    setConnectionsLoading(true);
    const q = query(
      collection(db, 'connections'),
      where('businessId', '==', activeBusinessId),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(c => c.status !== 'disconnected');
        // Newest first.
        docs.sort((a, b) => {
          const ta = a.createdAt?.toMillis?.() ?? 0;
          const tb = b.createdAt?.toMillis?.() ?? 0;
          return tb - ta;
        });
        setConnections(docs);
        setConnectionsLoading(false);
      },
      (err) => {
        console.error('[MerchantApp] connections subscription failed:', err);
        setConnections([]);
        setConnectionsLoading(false);
      }
    );
    return () => unsub();
  }, [loadingBusinesses, activeBusinessId]);

  // ---- Effect 5: Hydrate customer cache for new uids ----
  // When orders change, identify customerIds not yet cached and fetch
  // their /users/{uid} docs in parallel. Adds to customerCache on success.
  useEffect(() => {
    const toFetch = new Set();
    realOrders.forEach(o => {
      if (o.customerId && !(o.customerId in customerCache)) {
        toFetch.add(o.customerId);
      }
    });
    // 6.0d: also hydrate users referenced by connections (so the
    // Connects tab can show real names/cities instead of raw uids).
    connections.forEach(c => {
      if (c.userId && !(c.userId in customerCache)) {
        toFetch.add(c.userId);
      }
    });
    if (toFetch.size === 0) return undefined;
    let cancelled = false;
    (async () => {
      const updates = {};
      await Promise.all(Array.from(toFetch).map(async (uid) => {
        try {
          const snap = await getDoc(doc(db, 'users', uid));
          if (snap.exists()) {
            const d = snap.data();
            updates[uid] = {
              name: d.name || 'Customer',
              initials: d.initials || initialsFromName(d.name || ''),
              color: d.color || avatarColorForUid(uid),
              photoUrl: d.photoUrl || null,
              city: d.currentCity || d.city || '',
            };
          } else {
            updates[uid] = {
              name: 'Customer',
              initials: '?',
              color: avatarColorForUid(uid),
              photoUrl: null,
              city: '',
            };
          }
        } catch (err) {
          console.warn('[MerchantApp] customer fetch failed for', uid, err);
          updates[uid] = {
            name: 'Customer',
            initials: '?',
            color: avatarColorForUid(uid),
            photoUrl: null,
            city: '',
          };
        }
      }));
      if (!cancelled && Object.keys(updates).length > 0) {
        setCustomerCache(prev => ({ ...prev, ...updates }));
      }
    })();
    return () => { cancelled = true; };
  }, [realOrders, connections, customerCache]);

  // ---- Derived: business header (uses activeBusiness — Step 6.0d) ----
  // Switcher is wired: top bar shows the currently active shop, not a
  // hardcoded first-owned. ownedCount drives whether the switcher is
  // shown at all (single-shop owners see no chevron).
  //
  // Phase 2.3 C2 — logoImageUrl + coverUrl carry through to the top bar
  // so the uploaded photos render in v2m-biz-logo (replacing the emoji)
  // and the cover banner above. Both fall back to null when the field
  // isn't set on the business doc; the top-bar JSX uses truthy checks
  // so null cleanly falls through to the emoji + no-banner path.
  const business = useMemo(() => {
    if (!activeBusiness) {
      return {
        name: 'My business',
        logo: '🏪',
        logoBg: 'linear-gradient(135deg, #B8893A 0%, #D6B576 100%)',
        logoImageUrl: null,
        coverUrl: null,
        verified: false,
        city: '',
        locality: '',
        ownedCount: ownedBusinesses.length,
      };
    }
    return {
      name: activeBusiness.name || 'Business',
      logo: emojiForCategory(activeBusiness.category),
      logoBg: logoBgFromCategory(activeBusiness.category),
      logoImageUrl: activeBusiness.logoImageUrl || null,
      coverUrl: activeBusiness.coverUrl || null,
      verified: activeBusiness.status === 'verified',
      city: activeBusiness.city || '',
      locality: activeBusiness.locality || '',
      ownedCount: ownedBusinesses.length,
    };
  }, [activeBusiness, ownedBusinesses.length]);

  // ---- Derived: orders mapped to the UI shape components expect ----
  // OrderCard + OrdersTab + TodayTab were written for the mock shape:
  // { id, customerName, customerInitial, customerColor, items (count),
  //   total, status, placedAt (string), mode }.
  // We adapt real Firestore docs to that shape so the JSX stays as-is.
  const orders = useMemo(() => {
    const businessNameById = {};
    ownedBusinesses.forEach(b => { businessNameById[b.id] = b.name; });
    return realOrders.map(o => {
      const customer = customerCache[o.customerId] || {
        name: 'Loading…', initials: '?', color: '#C45A3F', photoUrl: null,
      };
      return {
        // Use Firestore docId for internal references; displayNumber for UI.
        id: o.id,
        displayNumber: o.displayNumber || o.id,
        // I2a: customerId exposed for MerchantChatList dedup. Not used
        // by existing order UI but harmless to carry through.
        customerId: o.customerId,
        customerName: customer.name,
        customerInitial: customer.initials,
        customerColor: customer.color,
        customerPhotoUrl: customer.photoUrl,
        items: Array.isArray(o.items) ? o.items.length : 0,
        // Pass through the real items array too, for the detail overlay
        itemsArray: Array.isArray(o.items) ? o.items : [],
        total: o.total || 0,
        status: o.status || 'placed',
        placedAt: relativeTime(o.placedAt) || '',
        mode: o.mode || 'pickup',
        // Extra for multi-business display
        businessId: o.businessId,
        businessName: businessNameById[o.businessId] || '',
        // Order detail extras
        deliveryAddress: o.deliveryAddress || '',
        notes: o.notes || '',
        cancelReason: o.cancelReason || '',
      };
    });
  }, [realOrders, ownedBusinesses, customerCache]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // ---- Path Z Commit 2: auto-open OrderDetailOverlay ----
  // When MerchantApp is entered from V2 OrderChat's View → button,
  // V2App passes initialOrderId. We open the overlay once orders have
  // loaded and the order is findable. A ref guard ensures we only
  // auto-open once per mount — closing the overlay shouldn't re-open
  // it, and orders updates (status changes etc) shouldn't either.
  const autoOpenedOverlayRef = useRef(false);
  useEffect(() => {
    if (autoOpenedOverlayRef.current) return;
    if (!initialOrderId) return;
    if (loadingOrders) return;
    const found = orders.find(o => o.id === initialOrderId);
    if (!found) {
      // Order not in the merchant's orders list — likely outside the
      // 50-doc window or belongs to a different owner. Flag as handled
      // so we don't retry forever as orders updates flow in.
      autoOpenedOverlayRef.current = true;
      return;
    }
    // Switch active business if the order belongs to a different one,
    // so Today-tab stats and Orders-tab filters reflect this context.
    if (found.businessId && found.businessId !== activeBusinessId) {
      setActiveBusinessId(found.businessId);
    }
    setSelectedOrderId(initialOrderId);
    setOverlay('order');
    setActiveTab('orders');
    autoOpenedOverlayRef.current = true;
  }, [initialOrderId, loadingOrders, orders, activeBusinessId]);

  // ---- Product mutations (Step 6.0b) ----
  // All three write to /businesses/{firstOwnedBusinessId}/products/...
  // Soft delete only — locked rules forbid deleteDoc on products.
  // Local state isn't touched directly; the onSnapshot subscription
  // (Effect 3) reflects the change on the next tick.

  async function toggleStock(productId) {
    const bizId = activeBusinessId;
    if (!bizId) return;
    // Session 6c2b — defensive gate. Catalog card hides toggle/delete
    // buttons when !bizActive; this catches any stray call.
    if (!bizActive) {
      setToast('Reactivate your Aangan Connect to edit products');
      return;
    }
    const current = products.find(p => p.id === productId);
    if (!current) return;
    try {
      await updateDoc(
        doc(db, 'businesses', bizId, 'products', productId),
        { inStock: !current.inStock, updatedAt: serverTimestamp() }
      );
    } catch (err) {
      console.error('[MerchantApp] toggleStock failed:', err);
      setToast(`Couldn't update: ${err.code || err.message || 'try again'}`);
    }
  }

  async function deleteProduct(productId) {
    // Session 6c2b — defensive gate.
    if (!bizActive) {
      setToast('Reactivate your Aangan Connect to delete products');
      return;
    }
    if (!window.confirm('Remove this product?')) return;
    const bizId = activeBusinessId;
    if (!bizId) return;
    // Phase 2.3 C3 — capture whether the product had a photo BEFORE the
    // soft-delete so we know whether to clean up storage + decrement
    // the lifetime counter. Reading from the local snapshot avoids an
    // extra Firestore read.
    const product = products.find(p => p.id === productId);
    const hadPhoto = !!product?.imageUrl;
    try {
      await updateDoc(
        doc(db, 'businesses', bizId, 'products', productId),
        { deletedAt: serverTimestamp() }
      );
      // Best-effort photo cleanup. Both calls fail-open: if either errors
      // we still proceed since the soft-delete already succeeded.
      if (hadPhoto) {
        await deleteProductImage(bizId, productId);
        await decrementProductImageCount(bizId);
      }
      setToast('Product removed');
    } catch (err) {
      console.error('[MerchantApp] deleteProduct failed:', err);
      setToast(`Couldn't remove: ${err.code || err.message || 'try again'}`);
    }
  }

  async function addProduct(newProduct) {
    const bizId = activeBusinessId;
    if (!bizId) {
      setToast('No business selected');
      return;
    }
    try {
      // Phase 2.3 C3 — if the overlay uploaded a photo before save, it
      // generated the doc ID itself so the storage path could be set
      // (businesses/{bizId}/products/{productId}.webp). Honour that ID
      // via setDoc; otherwise fall back to addDoc auto-ID (no photo).
      const { id: preGeneratedId, ...productFields } = newProduct;
      const docData = {
        ...productFields,
        // Rule requires request.resource.data.businessId == businessId
        // (the path wildcard) — without this the create fails with
        // permission-denied. The 9 seed products were written via admin
        // script that bypassed rules, so this gap was hidden until now.
        businessId: bizId,
        inStock: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        deletedAt: null,
      };
      if (preGeneratedId) {
        // Photo-first flow: photo is already in Storage under this id.
        await setDoc(
          doc(db, 'businesses', bizId, 'products', preGeneratedId),
          docData
        );
      } else {
        // No photo: simple addDoc with auto-id.
        await addDoc(
          collection(db, 'businesses', bizId, 'products'),
          docData
        );
      }
      // Bump the lifetime product-image counter ONLY when a photo was
      // uploaded. Storage upload already happened, so this just records
      // that the slot is taken on the business doc.
      if (newProduct.imageUrl) {
        await bumpProductImageCount(bizId);
      }
      setOverlay(null);
      setToast('Product added');
    } catch (err) {
      console.error('[MerchantApp] addProduct failed:', err);
      setToast(`Couldn't add: ${err.code || err.message || 'try again'}`);
    }
  }

  // Phase 2.5 / item #6 — Replace (or add) a photo on an EXISTING
  // product without delete-and-recreate. Uses the same storage path
  // as the original upload (businesses/{bizId}/products/{productId}.webp),
  // so re-uploading there overwrites in place. The new download URL
  // differs (token changes per upload) so we update the product doc
  // with the new imageUrl to bust client caches.
  //
  // Counter accounting: bumpProductImageCount only when this is the
  // product's FIRST photo (i.e. it had no imageUrl before). Replacing
  // an existing photo consumes no new quota.
  async function replaceProductPhoto(productId, file) {
    const bizId = activeBusinessId;
    if (!bizId || !currentUser?.id || !productId || !file) return;
    // Session 6c2b — defensive gate.
    if (!bizActive) {
      setToast('Reactivate your Aangan Connect to update product photos');
      return;
    }
    try {
      // Validate the file up-front so the user sees a clear error if
      // they picked a HEIC or oversized image — same validator used by
      // AddProductOverlay so behaviour is consistent.
      validateImageFile(file);

      const product = products.find(p => p.id === productId);
      const isFirstUpload = !product?.imageUrl;

      // First-time upload: gate behind the quota check. Replacements
      // never consume new quota so we skip the check on those.
      if (isFirstUpload) {
        const q = await checkProductImageQuota(bizId);
        if (q && q.allowed === false) {
          setToast("You've reached your product image limit.");
          return;
        }
      }

      setToast('Uploading photo…');
      const newUrl = await uploadProductImage(bizId, productId, file, currentUser.id);
      await updateDoc(
        doc(db, 'businesses', bizId, 'products', productId),
        { imageUrl: newUrl, updatedAt: serverTimestamp() }
      );
      if (isFirstUpload) {
        await bumpProductImageCount(bizId);
      }
      setToast('Photo updated');
    } catch (err) {
      console.error('[MerchantApp] replaceProductPhoto failed:', err);
      setToast(err?.message || `Couldn't update photo: ${err?.code || 'try again'}`);
    }
  }

  function openOrderDetail(orderId) {
    setSelectedOrderId(orderId);
    setOverlay('order');
  }

  // ---- Business profile mutation (Step 6.0c) ----
  // updateBusiness writes the patch to /businesses/{activeBusinessId}.
  // Patch shape is whatever the edit overlay produces (info vs address)
  // — overlay-side validation has already trimmed strings and dropped
  // empty values. Rules permit owner writes; verified/activated/ownerId
  // are NOT included in the patch (admin-controlled).
  async function updateBusiness(patch) {
    const bizId = activeBusinessId;
    if (!bizId) {
      setToast('No business selected');
      return;
    }
    try {
      await updateDoc(
        doc(db, 'businesses', bizId),
        { ...patch, updatedAt: serverTimestamp() }
      );
      // Optimistic local sync — Effect 1 (businesses fetch) only runs on
      // uid change, so without this the Manage hub would keep showing
      // stale fields until the user navigated away and back.
      setOwnedBusinesses(prev =>
        prev.map(b => b.id === bizId ? { ...b, ...patch } : b)
      );
      setOverlay(null);
      setToast('Saved');
    } catch (err) {
      console.error('[MerchantApp] updateBusiness failed:', err);
      setToast(`Couldn't save: ${err.code || err.message || 'try again'}`);
    }
  }

  // Phase 2.3 C2 — Silent variant for photo URL persistence.
  // Photo uploads commit IMMEDIATELY (file is already in Storage; the URL
  // needs to land in the business doc right away so the overlay preview
  // and merchant header refresh). This is decoupled from the main Save
  // button which only saves text fields. THROWS on failure so the caller
  // (EditBusinessOverlay) can surface it in the photo banner instead of
  // a generic toast.
  async function updateBusinessSilent(patch) {
    const bizId = activeBusinessId;
    if (!bizId) throw new Error('No business selected');
    await updateDoc(
      doc(db, 'businesses', bizId),
      { ...patch, updatedAt: serverTimestamp() }
    );
    setOwnedBusinesses(prev =>
      prev.map(b => b.id === bizId ? { ...b, ...patch } : b)
    );
  }

  // updateOrderStatus rewired to Firestore in Step 6.0a.
  // Status transitions allowed for merchant (per firestore.rules after 4a.1):
  //   confirmed → preparing | cancelled
  //   preparing → ready | cancelled
  //   ready → completed | cancelled
  // 'placed' transitions are handled by the onOrderCreated Cloud Function;
  // merchant can't act on a 'placed' order directly (rule blocks it).
  async function updateOrderStatus(orderId, newStatus, cancelReason = '') {
    const orderRef = doc(db, 'orders', orderId);
    const updates = { status: newStatus };
    if (newStatus === 'preparing') updates.preparingAt = serverTimestamp();
    if (newStatus === 'ready') updates.readyAt = serverTimestamp();
    if (newStatus === 'completed') updates.completedAt = serverTimestamp();
    if (newStatus === 'cancelled') {
      updates.cancelledAt = serverTimestamp();
      if (cancelReason) updates.cancelReason = cancelReason;
    }
    try {
      await updateDoc(orderRef, updates);
      setOverlay(null);
      const label = STATUS_CONFIG[newStatus]?.label || newStatus;
      setToast(`Order moved to ${label}`);
    } catch (err) {
      console.error('[MerchantApp] updateOrderStatus failed:', err);
      setToast(`Couldn't update order: ${err.code || err.message || 'try again'}`);
    }
  }

  // ---- Step 6.0d: active-business filter + Today dashboard stats ----
  // Switcher: multi-shop owners see only the active shop's data here.
  // Single-shop owners pass through unchanged (one biz = always active).
  const activeBusinessOrders = useMemo(() => {
    if (!activeBusinessId) return [];
    return orders.filter(o => o.businessId === activeBusinessId);
  }, [orders, activeBusinessId]);

  // "Today" = midnight local → now. Derived from the raw realOrders array
  // (not the UI-mapped `orders`) because we need intact Firestore Timestamp
  // objects on placedAt — the UI shape converts them to relative-time
  // strings. Stats match the prototype:
  //   ordersToday  = accepted+ orders placed today (excludes 'placed'/'cancelled')
  //   revenueToday = sum of those orders' totals (₹)
  //   awaitingYou  = orders in 'confirmed' or 'placed' (any time — merchant must act)
  const todayStats = useMemo(() => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const startMs = startOfDay.getTime();

    let ordersToday = 0;
    let revenueToday = 0;
    let awaitingYou = 0;
    const acceptedStatuses = ['confirmed', 'preparing', 'ready', 'completed'];

    realOrders.forEach(o => {
      if (activeBusinessId && o.businessId !== activeBusinessId) return;
      if (o.status === 'confirmed' || o.status === 'placed') awaitingYou += 1;
      // Today's accepted ones: by placedAt + status
      const placedMs = o.placedAt?.toMillis?.() ?? 0;
      if (placedMs >= startMs && acceptedStatuses.includes(o.status)) {
        ordersToday += 1;
        revenueToday += Number(o.total || 0);
      }
    });

    return { ordersToday, revenueToday, awaitingYou };
  }, [realOrders, activeBusinessId]);

  // Counts for tab badges + Today stats — derive from REAL statuses.
  // 'newOrderCount' is the "needs my attention" count for the ACTIVE biz
  // (drives the orders-tab badge so it follows the switcher).
  const newOrderCount = activeBusinessOrders.filter(o => o.status === 'confirmed' || o.status === 'placed').length;
  const activeOrderCount = activeBusinessOrders.filter(o => ['placed', 'confirmed', 'preparing', 'ready'].includes(o.status)).length;

  // I2a: chat-list takes over the screen — own back arrow, no bottom
  // nav, no merchant header. Back returns to 'today' (the canonical
  // landing tab). This early-return runs before the main MerchantApp
  // render so we don't fight CSS / z-index with the header strip.
  if (activeTab === 'chat-list') {
    return (
      <MerchantChatList
        orders={activeBusinessOrders}
        onBack={() => setActiveTab('today')}
        onOpenChat={(orderId) => {
          if (onOpenChat) {
            // Stub-cleanup follow-up: remember the tab we're leaving
            // from so the back-pop from order-chat returns the user
            // here (chat-list) instead of dumping them on Today.
            try { sessionStorage.setItem('v2_merchant_returnTab', 'chat-list'); } catch (err) { /* storage disabled */ }
            onOpenChat(orderId);
          } else {
            setToast('Chat unavailable in this view');
          }
        }}
      />
    );
  }

  // Item #13 Phase A Step 8 — merchant notifications inbox overlay.
  // Same takeover pattern as chat-list: own back arrow, no merchant
  // header / bottom nav, returns to 'today' on back. Items + handlers
  // come from the top-level subscription state.
  if (activeTab === 'notifications') {
    return (
      <NotificationsScreenV2
        items={notifItems}
        loading={notifLoading}
        onBack={() => setActiveTab('today')}
        onMarkAllRead={handleMarkAllNotifsRead}
        onTapNotif={handleTapNotif}
      />
    );
  }

  return (
    <div className="v2-merchant">
      {/* ============ COVER BANNER (Phase 2.3 C2) ============ */}
      {/*
        Renders only when a cover photo is uploaded. Sits between the
        Merchant-mode strip and the operational top bar so the cover
        becomes a branded header for the owner's working view. Customers
        see the same cover on their BusinessProfile (customer-side
        rendering wired in C2.5 once BusinessProfile.jsx is in hand).
      */}
      {business.coverUrl && (
        <div
          className="v2m-biz-cover-banner"
          aria-hidden="true"
          style={{ backgroundImage: `url("${business.coverUrl}")` }}
        />
      )}

      {/* ============ TOP BAR ============ */}
      <div className="v2m-top-bar">
        {/*
          Step 6.0d: header is tappable for multi-shop owners → opens
          BusinessSwitcherSheet. Single-shop owners (1 biz) see no
          chevron and the click is a no-op.
        */}
        <button
          className="v2m-biz-mini"
          onClick={business.ownedCount > 1 ? () => setShowSwitcher(true) : undefined}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: business.ownedCount > 1 ? 'pointer' : 'default',
            font: 'inherit',
            color: 'inherit',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flex: 1,
            minWidth: 0,
            WebkitTapHighlightColor: 'transparent',
          }}
          aria-label={business.ownedCount > 1 ? 'Switch business' : business.name}
        >
          {/*
            v2m-biz-logo renders the uploaded photo if business.logoImageUrl
            is set; otherwise falls back to the emoji on the category gradient
            (the V1 behavior, preserved). Photo replaces the gradient
            background since the image fills the tile.
          */}
          <div
            className="v2m-biz-logo"
            style={{ background: business.logoImageUrl ? 'transparent' : business.logoBg }}
          >
            {business.logoImageUrl ? (
              <img
                src={business.logoImageUrl}
                alt=""
                className="v2m-biz-logo-img"
              />
            ) : (
              business.logo
            )}
          </div>
          <div className="v2m-biz-info">
            <div className="v2m-biz-name-row">
              <span className="v2m-biz-name">{business.name}</span>
              {business.verified && (
                <span className="v2m-verified">
                  <Check size={9} strokeWidth={3} />
                </span>
              )}
              {business.ownedCount > 1 && (
                <ChevronRight
                  size={14}
                  style={{
                    transform: 'rotate(90deg)',
                    color: 'var(--ink-soft, #6E6F84)',
                    flexShrink: 0,
                  }}
                />
              )}
            </div>
            <div className="v2m-biz-meta">
              {isOpenToday ? (
                <span className="v2m-status-open">🟢 Open today</span>
              ) : (
                <span className="v2m-status-closed">🔴 Closed</span>
              )}
              {business.locality && (
                <>
                  <span>·</span>
                  <span>{business.locality}</span>
                </>
              )}
            </div>
          </div>
        </button>
        <button
          className="v2m-icon-btn"
          onClick={() => setActiveTab('chat-list')}
          aria-label="Chats"
          style={{ marginRight: 4 }}
        >
          <MessageSquare size={18} />
        </button>
        <button
          className="v2m-icon-btn"
          onClick={() => setActiveTab('notifications')}
          aria-label="Notifications"
          style={{ marginRight: 4 }}
        >
          <Bell size={18} />
          {unreadNotifCount > 0 && (
            <span className="v2m-bell-badge">{unreadNotifCount}</span>
          )}
        </button>
        {/* Phase 2.5 / C10b — Settings (Manage) icon. Moved out of the
            bottom nav so the 5-tab layout (Today/Orders/Wall/Catalog/
            Connects) breathes. Tap routes to the Manage tab content;
            the tab is no longer in the nav strip but the screen still
            exists. */}
        <button
          className="v2m-icon-btn"
          onClick={() => setActiveTab('manage')}
          aria-label="Manage business"
          style={{ marginRight: 4 }}
        >
          <Settings size={18} />
        </button>
        {/* Phase 2.5 / C10b — Switch to Aangan. Replaces the "MERCHANT
            MODE" strip at the top of the screen. RefreshCw icon (cycle)
            with no label keeps it visually light next to the other
            action icons. */}
        <button
          className="v2m-icon-btn"
          onClick={onBack}
          aria-label="Switch to Aangan"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* ============ TAB CONTENT ============ */}
      <div className="v2m-content">
        {/* U13: recentOrders below is today's orders only (placedAt >= midnight),
            newest first, capped at 4. It previously showed the latest 4
            regardless of date, so a quiet morning displayed last week's.
            activeBusinessOrders is already newest-first. */}
        {activeTab === 'today' && (
          <TodayTab
            stats={todayStats}
            recentOrders={activeBusinessOrders
              .filter(o => (o.placedAt?.toMillis?.() ?? 0) >= new Date().setHours(0, 0, 0, 0))
              .slice(0, 4)}
            isOpenToday={isOpenToday}
            onToggleOpen={handleToggleOpenToday}
            onOpenOrder={openOrderDetail}
            onAddProduct={handleOpenAddProduct}
            onSetToast={setToast}
            onGoTo={(tab, opts) => {
              setActiveTab(tab);
              // Phase 2.5 / C10b — Quick Action "New post" sends
              // { compose: true } so the composer opens immediately
              // after the tab switch. Other onGoTo calls just navigate.
              if (tab === 'wall' && opts?.compose) {
                handleOpenWallCompose();
              }
            }}
            onShareProfile={handleShareProfile}
            ownerName={currentUser?.name || currentUser?.firstName || ''}
            // Phase 6.0f — pass business so TodayTab can render the
            // SubscriptionBanner with the right trial dates.
            business={activeBusiness}
            // Session 6c2a — Subscription banner's Activate/Reactivate CTA
            // routes here; opens support chat with pre-filled reactivation
            // message. Owner can edit before sending.
            onOpenSupport={() => handleOpenSupportChat(buildReactivateMessage())}
            // Session 6c2b — Quick Actions (Add product, New post) hidden
            // when biz is inactive. Share profile stays visible since it's
            // a share intent, not a modification.
            bizActive={bizActive}
          />
        )}
        {activeTab === 'catalog' && (
          <CatalogTab
            products={products}
            loading={productsLoading}
            businessName={activeBusiness?.name || ''}
            onToggleStock={toggleStock}
            onDelete={deleteProduct}
            onAddProduct={handleOpenAddProduct}
            onReplacePhoto={replaceProductPhoto}
            onSetToast={setToast}
            // Session 6c2b — Add product + per-product actions gated when
            // biz is inactive. Existing products stay visible so owner
            // can see catalog state, but modification is blocked.
            bizActive={bizActive}
          />
        )}
        {activeTab === 'wall' && (
          // Phase 2.5 / C10b — Merchant wall tab
          <WallTab
            businessId={activeBusiness?.id}
            businessName={activeBusiness?.name || ''}
            ownerUid={currentUser?.id}
            onCompose={handleOpenWallCompose}
            onEdit={handleEditWallPost}
            onDeletePost={handleDeleteWallPost}
            onSetToast={setToast}
            // Session 6c2b — Compose button + per-post edit/delete gated
            // when biz is inactive. Existing posts stay visible.
            bizActive={bizActive}
          />
        )}
        {activeTab === 'connects' && (
          <ConnectsTab
            connections={connections}
            customerCache={customerCache}
            orders={activeBusinessOrders}
            loading={connectionsLoading}
            onSetToast={setToast}
          />
        )}
        {activeTab === 'orders' && (
          <OrdersTab
            orders={activeBusinessOrders}
            onOpenOrder={openOrderDetail}
            loadingOrders={loadingOrders}
            ownedBusinessesCount={ownedBusinesses.length}
          />
        )}
        {activeTab === 'manage' && (
          <ManageBusinessTab
            business={activeBusiness || null}
            currentUser={currentUser}
            onEdit={(mode) => setOverlay(mode === 'info' ? 'edit-info' : 'edit-address')}
            onViewPublicProfile={() => {
              const bizId = activeBusinessId;
              if (bizId) window.location.search = `?v2=biz-profile&id=${bizId}`;
            }}
            onSwitchToConsumer={onBack}
            onSetToast={setToast}
          />
        )}
      </div>

      {/* ============ BOTTOM NAV ============ */}
      {/* Order: Today / Orders / Wall / Catalog / Connects.
          Orders sits in slot 2 so the unread-orders badge is easy to
          glance at without scanning the whole nav; Catalog and Connects
          (lower-frequency surfaces) move right. */}
      <div className="v2m-bottom-nav">
        <button
          className={`v2m-nav-tab ${activeTab === 'today' ? 'active' : ''}`}
          onClick={() => setActiveTab('today')}
        >
          <Home size={18} />
          <span>Today</span>
        </button>
        <button
          className={`v2m-nav-tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          <Package size={18} />
          <span>Orders</span>
          {newOrderCount > 0 && (
            <span className="v2m-tab-badge">{newOrderCount}</span>
          )}
        </button>
        <button
          className={`v2m-nav-tab ${activeTab === 'wall' ? 'active' : ''}`}
          onClick={() => setActiveTab('wall')}
        >
          <Megaphone size={18} />
          <span>Wall</span>
        </button>
        <button
          className={`v2m-nav-tab ${activeTab === 'catalog' ? 'active' : ''}`}
          onClick={() => setActiveTab('catalog')}
        >
          <ShoppingBag size={18} />
          <span>Catalog</span>
        </button>
        <button
          className={`v2m-nav-tab ${activeTab === 'connects' ? 'active' : ''}`}
          onClick={() => setActiveTab('connects')}
        >
          <Users size={18} />
          <span>Connects</span>
        </button>
      </div>

      {/* ============ OVERLAYS ============ */}
      {(overlay === 'wall-compose' || overlay === 'wall-edit') && activeBusiness && (
        <WallPostComposer
          mode={overlay === 'wall-edit' ? 'edit' : 'create'}
          businessId={activeBusiness.id}
          businessName={activeBusiness.name || ''}
          ownerUid={currentUser?.id}
          existing={overlay === 'wall-edit' ? wallEditingPost : null}
          onClose={handleCloseWallComposer}
          onSaved={() => { /* WallTab's listener auto-refreshes */ }}
          onSetToast={setToast}
        />
      )}
      {overlay === 'add-product' && (
        <AddProductOverlay
          onClose={() => setOverlay(null)}
          onSave={addProduct}
          bizId={activeBusinessId}
          ownerUid={activeBusiness?.ownerId || currentUid}
        />
      )}

      {(overlay === 'edit-info' || overlay === 'edit-address') && (
        <EditBusinessOverlay
          mode={overlay === 'edit-info' ? 'info' : 'address'}
          business={activeBusiness || null}
          onClose={() => setOverlay(null)}
          onSave={updateBusiness}
          onPhotoSave={updateBusinessSilent}
        />
      )}

      {showSwitcher && (
        <BusinessSwitcherSheet
          businesses={ownedBusinesses}
          activeId={activeBusinessId}
          onPick={(id) => {
            setActiveBusinessId(id);
            setShowSwitcher(false);
          }}
          onClose={() => setShowSwitcher(false)}
        />
      )}

      {overlay === 'order' && selectedOrderId && (
        <OrderDetailOverlay
          order={orders.find(o => o.id === selectedOrderId)}
          onClose={() => setOverlay(null)}
          onUpdateStatus={updateOrderStatus}
          onChat={() => {
            setOverlay(null);
            if (onOpenChat) {
              // Stub-cleanup follow-up: remember the tab the overlay
              // was opened on, so back-pop from chat returns to that
              // tab (typically 'orders' or 'today' RECENT ORDERS).
              try { sessionStorage.setItem('v2_merchant_returnTab', activeTab); } catch (err) { /* storage disabled */ }
              onOpenChat(selectedOrderId);
            } else {
              setToast('Chat unavailable in this view');
            }
          }}
        />
      )}

      {/* Session 6c2a — Aangan Support chat modal. Opened by the
          SubscriptionBanner's Activate/Reactivate CTA with a pre-filled
          reactivation message. Closing the modal clears chatId AND
          prefill so a subsequent open starts clean (or with a new
          prefill). Reuses the same modal component as ManageBusinessTab
          but with its own instance. */}
      {supportChatId && (
        <SupportChatModal
          chatId={supportChatId}
          ownerUid={currentUid}
          prefillText={supportChatPrefill}
          onClose={() => {
            setSupportChatId(null);
            setSupportChatPrefill('');
          }}
        />
      )}

      {/* Toast */}
      {toast && <div className="v2m-toast">{toast}</div>}
    </div>
  );
}

// =====================================================================
// TODAY TAB
// =====================================================================

function TodayTab({ stats, recentOrders, isOpenToday, onToggleOpen, onOpenOrder, onAddProduct, onSetToast, onGoTo, onShareProfile, ownerName, business, onOpenSupport, bizActive }) {
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  // Friendly display name from currentUser. Falls back to "there" so the
  // sentence still reads naturally for users without a name set.
  const displayName = (ownerName && ownerName.trim())
    ? ownerName.trim().split(' ')[0]
    : 'there';

  return (
    <div className="v2m-tab-scroll">
      {/* Greeting */}
      <div className="v2m-greeting">
        <div className="v2m-greeting-label">{greeting},</div>
        <div className="v2m-greeting-name">{displayName} 👋</div>
      </div>

      {/* Phase 6.0f — Trial / subscription banner. Renders only when
          business has trialStartedAt set; legacy/seed records show nothing.
          Session 6c2a — Activate/Reactivate CTA now routes to the Aangan
          Support chat with a pre-filled message (via onOpenSupport). */}
      <SubscriptionBanner business={business} onActivate={onOpenSupport} />

      {/* Open/closed toggle card */}
      <div className={`v2m-open-card ${isOpenToday ? 'open' : 'closed'}`}>
        <div className="v2m-open-info">
          <div className="v2m-open-label">
            {isOpenToday ? 'You are open today' : 'You are closed today'}
          </div>
          <div className="v2m-open-sub">
            {isOpenToday
              ? 'Customers can place orders now'
              : 'Customers will see "Closed" on your profile'}
          </div>
        </div>
        <button
          className={`v2m-toggle ${isOpenToday ? 'on' : 'off'}`}
          onClick={onToggleOpen}
          aria-label="Toggle open"
        >
          <span className="v2m-toggle-knob" />
        </button>
      </div>

      {/* Stats grid (Step 6.0d — wired to real orders) */}
      {/*
        Three stats from real data, matching the prototype:
          - Orders today: accepted+ orders placed since midnight
          - Order value: sum of those orders' totals (₹)
          - Awaiting you: count of 'confirmed' or 'placed' orders (any time)
      */}
      <div className="v2m-stats-grid v2m-stats-grid-3">
        <div className="v2m-stat-card">
          <div className="v2m-stat-icon" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
            <Package size={16} />
          </div>
          <div className="v2m-stat-num">{stats.ordersToday}</div>
          <div className="v2m-stat-label">Orders today</div>
        </div>
        <div className="v2m-stat-card">
          <div className="v2m-stat-icon" style={{ background: 'var(--success-soft)', color: 'var(--success)' }}>
            <TrendingUp size={16} />
          </div>
          <div className="v2m-stat-num">
            {stats.revenueToday >= 1000
              ? `₹${(stats.revenueToday / 1000).toFixed(1)}k`
              : `₹${stats.revenueToday.toLocaleString('en-IN')}`}
          </div>
          <div className="v2m-stat-label">Order value</div>
        </div>
        <div className="v2m-stat-card">
          <div className="v2m-stat-icon" style={{ background: 'var(--gold-soft)', color: 'var(--gold)' }}>
            <Clock size={16} />
          </div>
          <div className="v2m-stat-num">{stats.awaitingYou}</div>
          <div className="v2m-stat-label">Awaiting you</div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="v2m-section">
        <div className="v2m-section-label">Quick actions</div>
        <div className="v2m-quick-actions">
          {/* Session 6c2b — Add product hidden when biz is inactive.
              Same restriction as CatalogTab's own Add product button. */}
          {bizActive && (
            <button className="v2m-quick-btn" onClick={onAddProduct}>
              <div className="v2m-quick-icon" style={{ background: 'var(--accent)' }}>
                <Plus size={16} />
              </div>
              <span>Add product</span>
            </button>
          )}
          <button
            className="v2m-quick-btn"
            onClick={onShareProfile}
          >
            <div className="v2m-quick-icon" style={{ background: 'var(--gold)' }}>
              <MessageSquare size={16} />
            </div>
            <span>Share profile</span>
          </button>
          {/* Session 6c2b — New post hidden when biz is inactive. */}
          {bizActive && (
            <button
              className="v2m-quick-btn"
              onClick={() => onGoTo('wall', { compose: true })}
            >
              <div className="v2m-quick-icon" style={{ background: 'var(--info)' }}>
                <Megaphone size={16} />
              </div>
              <span>New post</span>
            </button>
          )}
        </div>
      </div>

      {/* Recent orders */}
      <div className="v2m-section">
        <div className="v2m-section-label">
          Recent orders
          <button className="v2m-section-link" onClick={() => onGoTo('orders')}>
            See all <ChevronRight size={11} />
          </button>
        </div>
        <div className="v2m-orders-list">
          {recentOrders.length === 0 ? (
            <div className="v2m-empty">
              <div className="v2m-empty-icon">📦</div>
              <div className="v2m-empty-text">No orders yet today.</div>
            </div>
          ) : (
            recentOrders.map(o => (
              <OrderCard key={o.id} order={o} onClick={() => onOpenOrder(o.id)} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// CATALOG TAB
// =====================================================================

function CatalogTab({ products, loading, businessName, onToggleStock, onDelete, onAddProduct, onReplacePhoto, onSetToast, bizActive = true }) {
  const inStockCount = products.filter(p => p.inStock).length;
  const outOfStockCount = products.length - inStockCount;
  // C3.1 — fullscreen photo viewer state. Set to a URL to open; null = closed.
  const [viewerSrc, setViewerSrc] = useState(null);

  return (
    <div className="v2m-tab-scroll">
      <div className="v2m-cat-header">
        <div>
          <div className="v2m-cat-title">
            {businessName ? `${businessName} catalog` : 'Your catalog'}
          </div>
          <div className="v2m-cat-sub">
            {loading
              ? 'Loading…'
              : (<><b>{inStockCount}</b> in stock · <b>{outOfStockCount}</b> out of stock</>)}
          </div>
        </div>
        {/* Session 6c2b — Add button hidden when biz is inactive. */}
        {bizActive && (
          <button className="v2m-add-btn" onClick={onAddProduct}>
            <Plus size={14} /> Add
          </button>
        )}
      </div>

      {/* Session 6c2b — Inline hint replaces the Add button when biz is
          inactive. Warm-orange treatment matches SubscriptionBanner
          (critical palette). Explains WHY the button is missing so
          owner knows the path to unblock. */}
      {!bizActive && !loading && (
        <div style={{
          margin: '0 16px 12px',
          padding: '10px 14px',
          background: '#FDF1ED',
          border: '1px solid #E8C8BB',
          color: '#8A3A20',
          borderRadius: 10,
          fontSize: 12,
          lineHeight: 1.5,
        }}>
          Reactivate your Aangan Connect to add or edit products.
        </div>
      )}

      {loading ? (
        <div className="v2m-empty-state">
          <Loader2 size={28} className="v2m-spin" />
          <div className="v2m-empty-title" style={{ marginTop: 10 }}>Loading products…</div>
        </div>
      ) : products.length === 0 ? (
        <div className="v2m-empty-state">
          <div className="v2m-empty-icon">🛒</div>
          <div className="v2m-empty-title">No products yet</div>
          <div className="v2m-empty-sub">
            {bizActive
              ? 'Add your first product to start receiving orders.'
              : 'Reactivate your Aangan Connect to start adding products.'}
          </div>
          {/* Session 6c2b — Empty-state CTA hidden when biz is inactive.
              Hint at top of tab already explains reactivation path. */}
          {bizActive && (
            <button className="v2m-empty-cta" onClick={onAddProduct}>
              <Plus size={14} /> Add product
            </button>
          )}
        </div>
      ) : (
        <div className="v2m-products-list">
          {products.map(p => (
            <div key={p.id} className={`v2m-product-card ${!p.inStock ? 'out-of-stock' : ''}`}>
              {p.imageUrl ? (
                <button
                  type="button"
                  className="v2m-product-icon-tap"
                  onClick={() => setViewerSrc(p.imageUrl)}
                  aria-label="View product photo"
                >
                  <BusinessLogo
                    className="v2m-product-icon"
                    imageUrl={p.imageUrl}
                    emoji={p.icon}
                    bg={p.iconBg}
                  />
                </button>
              ) : (
                <BusinessLogo
                  className="v2m-product-icon"
                  imageUrl={p.imageUrl}
                  emoji={p.icon}
                  bg={p.iconBg}
                />
              )}
              <div className="v2m-product-info">
                <div className="v2m-product-name">{p.name}</div>
                <div className="v2m-product-meta">
                  ₹{p.price} · {p.unit}
                </div>
              </div>
              <div className="v2m-product-actions">
                {/* Session 6c2b — Per-product action buttons hidden when
                    biz is inactive. Product row (name, price, unit, photo
                    view) stays visible so owner can review catalog state.
                    Toggle stock / replace photo / delete return when the
                    biz is reactivated. */}
                {bizActive && (
                  <>
                    <button
                      className={`v2m-stock-toggle ${p.inStock ? 'in' : 'out'}`}
                      onClick={() => onToggleStock(p.id)}
                    >
                      {p.inStock ? (
                        <><Eye size={11} /> In stock</>
                      ) : (
                        <><EyeOff size={11} /> Out of stock</>
                      )}
                    </button>
                    {/* Item #6 — Replace (or add) product photo without
                        delete-and-recreate. <label> wraps a hidden file input
                        so each card scopes its own picker; no ref management
                        needed. aria-label adapts to whether the product
                        currently has a photo. */}
                    <label
                      className="v2m-product-edit-photo"
                      aria-label={p.imageUrl ? 'Replace photo' : 'Add photo'}
                      title={p.imageUrl ? 'Replace photo' : 'Add photo'}
                      style={{
                        width: 28, height: 28, borderRadius: 999,
                        background: 'transparent', border: '1px solid var(--border)',
                        color: 'var(--ink-soft)', display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', marginLeft: 4,
                      }}
                    >
                      <Camera size={13} />
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        style={{ display: 'none' }}
                        onChange={e => {
                          const file = e.target.files?.[0];
                          e.target.value = '';
                          if (file && onReplacePhoto) onReplacePhoto(p.id, file);
                        }}
                      />
                    </label>
                    <button
                      className="v2m-product-del"
                      onClick={() => onDelete(p.id)}
                      aria-label="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Phase 2.3 C3.1 — fullscreen photo viewer for product images. */}
      <ImageViewerModal
        open={!!viewerSrc}
        src={viewerSrc}
        onClose={() => setViewerSrc(null)}
      />
    </div>
  );
}

// =====================================================================
// CONNECTS TAB
// =====================================================================

function ConnectsTab({ connections, customerCache, orders, loading, onSetToast }) {
  const [search, setSearch] = useState('');

  // Hydrate each connection with customer info + per-customer order count.
  // 6.0d: orders prop is the active-business orders array; count is "how
  // many orders this customer placed at this shop", a useful merchant signal.
  const enriched = useMemo(() => {
    const ordersByCustomer = {};
    orders.forEach(o => {
      ordersByCustomer[o.customerId] = (ordersByCustomer[o.customerId] || 0) + 1;
    });
    return connections.map(c => {
      const cust = customerCache[c.userId] || {};
      return {
        id: c.id,
        userId: c.userId,
        name: cust.name || 'Customer',
        initial: cust.initials || '?',
        color: cust.color || '#C45A3F',
        city: cust.city || '',
        connectedAt: relativeTime(c.createdAt) || '',
        orders: ordersByCustomer[c.userId] || 0,
      };
    });
  }, [connections, customerCache, orders]);

  const filtered = useMemo(() => {
    if (!search.trim()) return enriched;
    const q = search.trim().toLowerCase();
    return enriched.filter(c =>
      c.name.toLowerCase().includes(q) || c.city.toLowerCase().includes(q)
    );
  }, [enriched, search]);

  const totalOrders = enriched.reduce((sum, c) => sum + c.orders, 0);

  return (
    <div className="v2m-tab-scroll">
      <div className="v2m-cat-header">
        <div>
          <div className="v2m-cat-title">Your customers</div>
          <div className="v2m-cat-sub">
            {loading
              ? 'Loading…'
              : (<><b>{enriched.length}</b> connected · <b>{totalOrders}</b> orders total</>)}
          </div>
        </div>
      </div>

      <div className="v2m-search-bar">
        <input
          type="text"
          className="v2m-search-input"
          placeholder="Search customers…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="v2m-empty-state">
          <Loader2 size={28} className="v2m-spin" />
          <div className="v2m-empty-title" style={{ marginTop: 10 }}>Loading customers…</div>
        </div>
      ) : enriched.length === 0 ? (
        <div className="v2m-empty">
          <div className="v2m-empty-icon">👥</div>
          <div className="v2m-empty-text">No customers have connected yet</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="v2m-empty">
          <div className="v2m-empty-icon">🔍</div>
          <div className="v2m-empty-text">No customers match "{search}"</div>
        </div>
      ) : (
        <div className="v2m-connects-list">
          {filtered.map(c => (
            <button
              key={c.id}
              className="v2m-connect-row"
              onClick={() => onSetToast(`Opens chat with ${c.name}`)}
            >
              <div className="v2m-connect-avatar" style={{ background: c.color }}>
                {c.initial}
              </div>
              <div className="v2m-connect-info">
                <div className="v2m-connect-name">{c.name}</div>
                <div className="v2m-connect-meta">
                  <MapPin size={10} />{' '}
                  {c.city ? `${c.city} · ` : ''}
                  Since {c.connectedAt}
                </div>
              </div>
              <div className="v2m-connect-orders">
                <div className="v2m-connect-orders-num">{c.orders}</div>
                <div className="v2m-connect-orders-label">orders</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// ORDERS TAB
// =====================================================================

function OrdersTab({ orders, onOpenOrder, loadingOrders, ownedBusinessesCount }) {
  const [filter, setFilter] = useState('all');

  // Filters now map directly to real order statuses (Step 6.0d polish).
  // 'Action needed' groups placed + confirmed since both need merchant
  // attention before fulfilment work begins. Other tabs are 1:1 with a
  // single status — labels match the badges shown on each order card,
  // so a merchant can quickly "show me only Preparing" or "only Ready".
  const visible = useMemo(() => {
    if (filter === 'all') return orders;
    if (filter === 'action') return orders.filter(o => o.status === 'placed' || o.status === 'confirmed');
    return orders.filter(o => o.status === filter);
  }, [orders, filter]);

  const counts = {
    all:       orders.length,
    action:    orders.filter(o => o.status === 'placed' || o.status === 'confirmed').length,
    preparing: orders.filter(o => o.status === 'preparing').length,
    ready:     orders.filter(o => o.status === 'ready').length,
    completed: orders.filter(o => o.status === 'completed').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
  };

  const FILTERS = [
    { id: 'all',       label: 'All' },
    { id: 'action',    label: 'Action needed' },
    { id: 'preparing', label: 'Preparing' },
    { id: 'ready',     label: 'Ready' },
    { id: 'completed', label: 'Completed' },
    { id: 'cancelled', label: 'Cancelled' },
  ];

  // Subtitle counts: "active" (anything pre-completed) + "done" (completed only).
  const activeCount = orders.filter(o => ['placed', 'confirmed', 'preparing', 'ready'].includes(o.status)).length;

  return (
    <div className="v2m-tab-scroll">
      <div className="v2m-cat-header">
        <div>
          <div className="v2m-cat-title">Orders</div>
          <div className="v2m-cat-sub">
            <b>{activeCount}</b> active · <b>{counts.completed}</b> done
          </div>
        </div>
      </div>

      <div className="v2m-order-filters">
        {FILTERS.map(f => (
          <button
            key={f.id}
            className={`v2m-order-filter ${filter === f.id ? 'active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            <span className="v2m-order-filter-count">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      <div className="v2m-orders-list">
        {loadingOrders ? (
          <div className="v2m-empty">
            <div className="v2m-empty-icon"><Loader2 size={32} /></div>
            <div className="v2m-empty-text">Loading orders…</div>
          </div>
        ) : ownedBusinessesCount === 0 ? (
          <div className="v2m-empty">
            <div className="v2m-empty-icon">🏪</div>
            <div className="v2m-empty-text">
              You don't own any verified businesses yet.<br/>
              Get listed on Aangan to start receiving orders.
            </div>
          </div>
        ) : visible.length === 0 ? (
          <div className="v2m-empty">
            <div className="v2m-empty-icon">📦</div>
            <div className="v2m-empty-text">No orders in this filter.</div>
          </div>
        ) : (
          visible.map(o => (
            <OrderCard key={o.id} order={o} onClick={() => onOpenOrder(o.id)} />
          ))
        )}
      </div>
    </div>
  );
}

// =====================================================================
// SHARED — ORDER CARD
// =====================================================================

function OrderCard({ order, onClick }) {
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.placed;
  return (
    <button className={`v2m-order-card v2m-order-${order.status}`} onClick={onClick}>
      <div className="v2m-order-top">
        <div className="v2m-order-customer">
          {order.customerPhotoUrl ? (
            <img
              className="v2m-order-avatar"
              src={order.customerPhotoUrl}
              alt={order.customerName}
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <div className="v2m-order-avatar" style={{ background: order.customerColor }}>
              {order.customerInitial}
            </div>
          )}
          <div>
            <div className="v2m-order-name">{order.customerName}</div>
            <div className="v2m-order-id">#{order.displayNumber || order.id}</div>
            {order.businessName && (
              <div className="v2m-order-biz" style={{ fontSize: 11, color: 'var(--text-muted, #6E6E78)', marginTop: 2 }}>
                {order.businessName}
              </div>
            )}
          </div>
        </div>
        <span className={`v2m-order-status status-${cfg.color}`}>
          {cfg.icon} {cfg.label}
        </span>
      </div>
      <div className="v2m-order-bottom">
        <span>{order.items} {order.items === 1 ? 'item' : 'items'} · {order.mode}</span>
        <span className="v2m-order-total">₹{order.total.toLocaleString('en-IN')}</span>
      </div>
      <div className="v2m-order-time">{order.placedAt}</div>
    </button>
  );
}

// =====================================================================
// ADD PRODUCT OVERLAY
// =====================================================================

function AddProductOverlay({ onClose, onSave, bizId, ownerUid }) {
  const [form, setForm] = useState({
    name: '',
    unit: '',
    price: '',
    icon: '🍯',
    iconBg: 'linear-gradient(135deg, #C45A3F 0%, #E8A57A 100%)',
  });

  const PRESET_ICONS = ['🍯', '💎', '🍩', '⚪', '🟡', '🥨', '🍰', '🧁', '☕', '🥤'];

  // ---- Phase 2.3 C3 — product photo upload state ----
  // photoFile: raw picked File ready to upload (held in memory until Save)
  // photoPreview: data URL for in-overlay preview (created at pick time)
  // The photo isn't uploaded to Storage until the user taps Add product,
  // so cancelling the overlay leaves zero Storage bytes behind.
  //
  // C3.1 — no crop step for products. The file is taken as-is; the
  // catalog tile's CSS `object-fit: cover` centre-crops to display.
  // Logo + cover keep their crop step (fixed-aspect containers); only
  // product upload is no-crop.
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [quota, setQuota] = useState({ remaining: null });
  const [photoMsg, setPhotoMsg] = useState('');

  // Fetch the product-image quota on mount so the UI can show the
  // "X left" hint when remaining is low. fail-open via the helper.
  useEffect(() => {
    if (!bizId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const q = await checkProductImageQuota(bizId);
        if (!cancelled) setQuota(q);
      } catch (e) {
        console.warn('Could not load product image quota:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [bizId]);

  function handlePhotoFilePicked(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (quota.allowed === false) {
      setPhotoMsg("You've reached your product image limit.");
      return;
    }
    // C3.1 — no crop step. Validate up-front so the user sees the
    // "only JPEG/PNG/WebP" / size-too-big errors at pick time, not
    // hidden inside the save step.
    try {
      validateImageFile(file);
    } catch (err) {
      setPhotoMsg(err.message || 'Could not use that file.');
      return;
    }
    setPhotoMsg('');
    setPhotoFile(file);
    // Generate a preview data URL for the overlay. Same FileReader
    // dance the crop modal used; just no crop step in between.
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.onerror = () => setPhotoMsg('Could not read that file.');
    reader.readAsDataURL(file);
  }

  function handleRemovePhoto() {
    setPhotoFile(null);
    setPhotoPreview('');
    setPhotoMsg('');
  }

  async function handleSubmit() {
    if (!form.name.trim() || !form.unit.trim() || !form.price) {
      alert('Please fill all fields');
      return;
    }
    if (saving) return;
    setSaving(true);
    setPhotoMsg('');

    try {
      // Phase 2.3 C3 — when a photo is attached, pre-generate the product
      // doc ID so we can upload to businesses/{bizId}/products/{id}.webp
      // BEFORE the doc is written. If the upload fails, the parent's
      // addProduct is never called, and the user can retry without an
      // orphan doc. If the upload succeeds but addProduct fails, we have
      // an orphan storage file (cleaned up by deleteProductImage if the
      // user later retries) — acceptable trade-off for a much simpler
      // single-action UX (one tap commits everything).
      let imageUrl = null;
      let preGeneratedId = null;
      if (photoFile) {
        if (!bizId || !ownerUid) {
          throw new Error('Missing business or owner info — try reopening this form.');
        }
        const newProductRef = doc(collection(db, 'businesses', bizId, 'products'));
        preGeneratedId = newProductRef.id;
        imageUrl = await uploadProductImage(bizId, preGeneratedId, photoFile, ownerUid);
      }

      // Hand off to parent. Parent uses setDoc(...preGeneratedId) when an
      // id is present (photo path), falls back to addDoc otherwise.
      // Parent also bumps the lifetime counter when imageUrl is set.
      await onSave({
        ...(preGeneratedId ? { id: preGeneratedId } : {}),
        name: form.name.trim(),
        unit: form.unit.trim(),
        price: parseInt(form.price, 10),
        icon: form.icon,
        iconBg: form.iconBg,
        ...(imageUrl ? { imageUrl } : {}),
      });
      // Parent closes the overlay via setOverlay(null) on success.
    } catch (err) {
      console.error('Product save failed:', err);
      setPhotoMsg(err?.message || "Couldn't save product. Try again.");
      setSaving(false);
    }
  }

  // Hide the emoji picker grid once a photo is queued — same pattern as
  // the merchant logo flow ("Use icon instead" brings it back). Keeps
  // the form scannable when a photo is the user's clear choice.
  const showEmojiPicker = !photoFile;

  return (
    <div className="v2m-overlay-backdrop" onClick={saving ? undefined : onClose}>
      <div className="v2m-overlay-sheet" onClick={e => e.stopPropagation()}>
        <div className="v2m-overlay-handle" />
        <div className="v2m-overlay-header">
          <div className="v2m-overlay-title">Add product</div>
          <button className="v2m-overlay-close" onClick={onClose} aria-label="Close" disabled={saving}>
            <X size={18} />
          </button>
        </div>

        <div className="v2m-overlay-body">
          {photoMsg && (
            <div className="v2m-photo-banner" role="status">
              {photoMsg}
            </div>
          )}

          {/* ===== PRODUCT IMAGE ===== */}
          <div className="v2m-form-row">
            <label className="v2m-form-label">Product image</label>
            <div className="v2m-logo-edit-row">
              <div
                className="v2m-logo-edit-preview"
                style={{ background: photoPreview ? 'transparent' : form.iconBg }}
              >
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt=""
                    className="v2m-logo-edit-img"
                  />
                ) : (
                  <span className="v2m-logo-edit-emoji">{form.icon}</span>
                )}
              </div>
              <div className="v2m-logo-edit-actions">
                <button
                  type="button"
                  className="v2m-photo-btn-secondary"
                  onClick={() => fileRef.current?.click()}
                  disabled={saving || quota.allowed === false}
                >
                  {photoFile ? 'Change photo' : 'Upload photo'}
                </button>
                {photoFile && (
                  <button
                    type="button"
                    className="v2m-photo-btn-link"
                    onClick={handleRemovePhoto}
                    disabled={saving}
                  >
                    Use icon instead
                  </button>
                )}
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={handlePhotoFilePicked}
            />
            {quota.remaining !== null && quota.remaining <= 3 && (
              <div className="v2m-form-hint v2m-hint-warning">
                {quota.remaining === 0
                  ? 'No more product images available. Remove an existing product to free a slot.'
                  : `${quota.remaining} product image${quota.remaining === 1 ? '' : 's'} left.`}
              </div>
            )}
            {showEmojiPicker && (
              <>
                <div className="v2m-icon-grid">
                  {PRESET_ICONS.map(ico => (
                    <button
                      key={ico}
                      type="button"
                      className={`v2m-icon-opt ${form.icon === ico ? 'sel' : ''}`}
                      onClick={() => setForm(f => ({ ...f, icon: ico }))}
                    >
                      {ico}
                    </button>
                  ))}
                </div>
                <div className="v2m-form-hint">Pick an icon, or upload a photo above</div>
              </>
            )}
          </div>

          <div className="v2m-form-row">
            <label className="v2m-form-label">Product name</label>
            <input
              type="text"
              className="v2m-form-input"
              placeholder="e.g. Kaju Katli"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              maxLength={60}
              disabled={saving}
            />
          </div>

          <div className="v2m-form-row">
            <label className="v2m-form-label">Unit / size</label>
            <input
              type="text"
              className="v2m-form-input"
              placeholder="e.g. 250 g box, 12 pcs"
              value={form.unit}
              onChange={e => setForm(f => ({ ...f, unit: e.target.value }))}
              maxLength={40}
              disabled={saving}
            />
          </div>

          <div className="v2m-form-row">
            <label className="v2m-form-label">Price (₹)</label>
            <input
              type="number"
              className="v2m-form-input"
              placeholder="e.g. 350"
              value={form.price}
              onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
              min={0}
              disabled={saving}
            />
          </div>
        </div>

        <div className="v2m-overlay-footer">
          <button className="v2m-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="v2m-btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? (photoFile ? 'Uploading…' : 'Saving…') : 'Add product'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// ORDER DETAIL OVERLAY
// =====================================================================

function OrderDetailOverlay({ order, onClose, onUpdateStatus, onChat }) {
  if (!order) return null;
  const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.placed;

  // Real status flow (no 'new' / Accept step — Cloud Function auto-confirms):
  //   placed     → no action; show "Verifying…" hint
  //   confirmed  → Mark preparing
  //   preparing  → Mark ready
  //   ready      → Mark completed
  //   completed  → no action
  //   cancelled  → no action
  const nextActionLabel = (() => {
    if (order.status === 'confirmed') return 'Start preparing';
    if (order.status === 'preparing') return 'Mark ready';
    if (order.status === 'ready')     return 'Mark completed';
    return null;
  })();
  const nextStatus = (() => {
    if (order.status === 'confirmed') return 'preparing';
    if (order.status === 'preparing') return 'ready';
    if (order.status === 'ready')     return 'completed';
    return null;
  })();
  const canCancel = ['confirmed', 'preparing', 'ready'].includes(order.status);

  return (
    <div className="v2m-overlay-backdrop" onClick={onClose}>
      <div className="v2m-overlay-sheet" onClick={e => e.stopPropagation()}>
        <div className="v2m-overlay-handle" />
        <div className="v2m-overlay-header">
          <div>
            <div className="v2m-overlay-title">Order details</div>
            {/* U3: was #{order.id} — the raw Firestore document id, e.g.
                #5O7RbymSjskni1ySAjHJ. An internal identifier has no business
                on a merchant's screen. The order object already carries
                displayNumber (AGN-XXXX); OrderCard above renders it
                correctly, this overlay simply reached for the wrong field.
                The || fallback covers legacy orders written before display
                numbers existed. */}
            <div className="v2m-overlay-sub">#{order.displayNumber || order.id}</div>
          </div>
          <button className="v2m-overlay-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="v2m-overlay-body">
          <div className="v2m-od-status">
            <span className={`v2m-od-status-pill status-${cfg.color}`}>
              {cfg.icon} {cfg.label}
            </span>
            <span className="v2m-od-status-time">{order.placedAt}</span>
          </div>

          <div className="v2m-od-section">
            <div className="v2m-od-section-label">Customer</div>
            <div className="v2m-od-customer">
              <div className="v2m-od-avatar" style={{ background: order.customerColor }}>
                {order.customerInitial}
              </div>
              <div className="v2m-od-customer-info">
                <div className="v2m-od-customer-name">{order.customerName}</div>
                <div className="v2m-od-customer-meta">Connected since Dec 2025</div>
              </div>
              <button className="v2m-od-chat-btn" onClick={onChat}>
                <MessageSquare size={11} /> Chat
              </button>
            </div>
          </div>

          <div className="v2m-od-section">
            <div className="v2m-od-section-label">Order · {order.items} {order.items === 1 ? 'item' : 'items'}</div>
            <div className="v2m-od-items">
              {(order.itemsArray || []).map((it, idx) => (
                <div key={it.productId || idx} className="v2m-od-item">
                  <span>{it.quantity || 1}× {it.name || it.productId}</span>
                  <span>₹{((it.price || 0) * (it.quantity || 1)).toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="v2m-od-section">
            <div className="v2m-od-section-label">{order.mode === 'delivery' ? 'Delivery to' : 'Pickup'}</div>
            <div className="v2m-od-block">
              {order.mode === 'delivery'
                ? (order.deliveryAddress || 'Address not provided.')
                : 'Customer will pick up from your shop. Share preferred timing via chat.'
              }
            </div>
          </div>

          {order.notes && (
            <div className="v2m-od-section">
              <div className="v2m-od-section-label">Customer note</div>
              <div className="v2m-od-block">{order.notes}</div>
            </div>
          )}

          {order.status === 'cancelled' && order.cancelReason && (
            <div className="v2m-od-section">
              <div className="v2m-od-section-label">Cancellation reason</div>
              <div className="v2m-od-block">
                {order.cancelReason === 'price_validation_failed'
                  ? 'Price/availability mismatch (auto-cancelled by Aangan to protect the customer).'
                  : order.cancelReason}
              </div>
            </div>
          )}

          <div className="v2m-od-section">
            <div className="v2m-od-section-label">Total</div>
            <div className="v2m-od-total">₹{order.total.toLocaleString('en-IN')}</div>
            <div className="v2m-od-payment-hint">
              💬 Share UPI / payment details with the customer via chat after accepting.
            </div>
          </div>
        </div>

        <div className="v2m-overlay-footer">
          {order.status === 'placed' && (
            <div style={{ color: 'var(--text-muted, #6E6E78)', fontSize: 13, flex: 1 }}>
              ⏳ Verifying prices… available in a moment.
            </div>
          )}
          {canCancel && (
            <button
              className="v2m-btn-ghost"
              style={{ color: 'var(--danger)' }}
              onClick={() => {
                const reason = window.prompt('Cancellation reason (visible to the customer):', 'Out of stock');
                if (reason === null) return;   // user pressed cancel
                onUpdateStatus(order.id, 'cancelled', reason.trim() || 'Cancelled by business');
              }}
            >
              Cancel order
            </button>
          )}
          {nextActionLabel && nextStatus ? (
            <button
              className="v2m-btn-primary"
              onClick={() => onUpdateStatus(order.id, nextStatus)}
            >
              {nextActionLabel}
            </button>
          ) : (
            <button className="v2m-btn-primary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
}
// =====================================================================
// EDIT BUSINESS OVERLAY (Step 6.0c)
// One generic overlay covers both 'info' and 'address' edit modes.
// Field set switches based on `mode`; parent's onSave receives only the
// editable patch (verified/activated/ownerId never sent — admin-only).
// =====================================================================

const CATEGORY_OPTIONS = [
  { id: 'food_beverages', label: 'Food & Beverages' },
  { id: 'apparel',        label: 'Fashion & Apparel' },
  { id: 'services',       label: 'Services' },
  { id: 'groceries',      label: 'Groceries' },
  { id: 'electronics',    label: 'Electronics' },
  { id: 'health_beauty',  label: 'Health & Beauty' },
  { id: 'home_decor',     label: 'Home & Décor' },
];

const LOGO_PRESETS = [
  '🏪', '🍯', '👗', '🔧', '🛒', '📱', '💄', '🏡',
  '🍰', '👜', '🪑', '🌱', '☕', '💎', '🎨', '🛍️',
];

// =====================================================================
// PHOTO CROP MODAL (Phase 2.3 C2)
//
// Duplicated from V1's PhotoCropModal (App.jsx L415) so that touching
// V1 isn't necessary to ship V2 photo uploads. Behavior matches V1:
//   - react-easy-crop based UI
//   - Outputs a JPEG File at 92% quality (downstream uploadBusiness*
//     helpers WebP-compress further via compressImage)
//   - Aspect + cropShape configurable per surface (logo = 1, round;
//     cover = 16/9, rect)
//
// Future: extract this and V1's copy to src/components/PhotoCropModal.jsx
// shared file. Tracked as a small polish refactor.
// =====================================================================

const getCroppedBlob = (imageSrc, pixelCrop) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return reject(new Error('Canvas not supported'));
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
      );
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Cropping failed'));
          const croppedFile = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
          resolve(croppedFile);
        },
        'image/jpeg',
        0.92
      );
    };
    image.onerror = () => reject(new Error('Could not load image for cropping'));
    image.src = imageSrc;
  });
};

function PhotoCropModal({ open, imageSrc, aspect = 1, cropShape = 'round', title = 'Position your photo', onClose, onDone }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedPixels(null);
      setBusy(false);
    }
  }, [open]);

  const onCropComplete = useCallback((_areaPct, areaPx) => {
    setCroppedPixels(areaPx);
  }, []);

  async function handleDone() {
    if (!croppedPixels || busy) return;
    setBusy(true);
    try {
      const file = await getCroppedBlob(imageSrc, croppedPixels);
      await onDone(file);
    } catch (e) {
      console.error('Crop failed:', e);
      alert(e?.message || 'Could not crop image');
      setBusy(false);
    }
    // Don't reset busy on success — parent closes the modal.
  }

  if (!open) return null;

  return (
    <div className="v2m-crop-backdrop" onClick={busy ? undefined : onClose}>
      <div className="v2m-crop-sheet" onClick={e => e.stopPropagation()}>
        <div className="v2m-crop-title">{title}</div>
        <div className="v2m-crop-canvas">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={cropShape}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="v2m-crop-zoom-row">
          <span className="v2m-crop-zoom-label">Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={e => setZoom(parseFloat(e.target.value))}
            className="v2m-crop-zoom-slider"
          />
        </div>
        <div className="v2m-crop-actions">
          <button className="v2m-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="v2m-btn-primary" onClick={handleDone} disabled={busy || !croppedPixels}>
            {busy ? 'Saving…' : 'Use photo'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// ImageViewerModal — moved to src/components/ImageViewerModal.jsx so
// the customer-side BusinessProfile can use the same component. Import
// is up at the top of this file (alongside BusinessLogo).
// =====================================================================

function EditBusinessOverlay({ mode, business, onClose, onSave, onPhotoSave }) {
  // Initial form values depend on mode and the business being edited.
  // useState init function so we don't re-derive on every render.
  //
  // Phase 2.3 C2 — logoImageUrl and coverUrl are NOT in form state.
  // They live on the business prop directly and are persisted on upload
  // (separate code path from the main Save button). This avoids the
  // common bug where uploading a photo, then tapping Cancel, leaves
  // an orphaned URL in form state but Firestore was already updated.
  const [form, setForm] = useState(() => {
    if (mode === 'info') {
      return {
        name: business?.name || '',
        shortDescription: business?.shortDescription || '',
        description: business?.description || '',
        category: business?.category || 'services',
        logo: business?.logo || '🏪',
      };
    }
    // mode === 'address'
    return {
      locality: business?.locality || '',
      city: business?.city || '',
      deliveryInstructions: business?.deliveryInstructions || '',
    };
  });
  const [saving, setSaving] = useState(false);

  // ---- Phase 2.3 C2 — photo upload state ----
  // Limits are fetched on mount (cheap: one Firestore read per surface).
  // shape: { allowed, used, limit, remaining } | { remaining: null } until loaded.
  const [logoLimit, setLogoLimit] = useState({ remaining: null });
  const [coverLimit, setCoverLimit] = useState({ remaining: null });
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const logoFileRef = useRef(null);
  const coverFileRef = useRef(null);
  // Crop modal state — `kind` tells onCropDone which surface to write to.
  const [cropState, setCropState] = useState({
    open: false,
    imageSrc: '',
    aspect: 1,
    cropShape: 'round',
    title: '',
    kind: null, // 'logo' | 'cover'
  });
  // Quick error/info banner shown above form rows (avoids alert() in modals).
  const [photoMsg, setPhotoMsg] = useState('');

  // Fetch limits on mount (info mode only — address mode has no photos).
  useEffect(() => {
    if (mode !== 'info' || !business?.id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [logo, cover] = await Promise.all([
          checkBusinessLogoLimit(business.id),
          checkBusinessCoverLimit(business.id),
        ]);
        if (cancelled) return;
        setLogoLimit(logo);
        setCoverLimit(cover);
      } catch (e) {
        // checkBusinessXxxLimit is itself fail-open; this catch is belt-and-
        // suspenders. Leave limits at their initial "unknown" shape on error.
        console.warn('Could not load photo limits:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, business?.id]);

  // ---- File picker → crop modal flow ----
  function openCropForLogo(imageSrc) {
    setCropState({
      open: true,
      imageSrc,
      aspect: 1,
      cropShape: 'round',
      title: 'Crop your logo',
      kind: 'logo',
    });
  }
  function openCropForCover(imageSrc) {
    setCropState({
      open: true,
      imageSrc,
      aspect: 16 / 9,
      cropShape: 'rect',
      title: 'Crop your cover photo',
      kind: 'cover',
    });
  }

  function handleLogoFilePicked(e) {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so the same file can be re-picked after cancel
    if (!file) return;
    if (logoLimit.allowed === false) {
      setPhotoMsg("You've used all your logo changes for this month.");
      return;
    }
    setPhotoMsg('');
    // Read into data URL so the crop modal can render it without a network fetch.
    const reader = new FileReader();
    reader.onload = () => openCropForLogo(reader.result);
    reader.onerror = () => setPhotoMsg('Could not read that file.');
    reader.readAsDataURL(file);
  }

  function handleCoverFilePicked(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (coverLimit.allowed === false) {
      setPhotoMsg("You've used all your cover changes for this month.");
      return;
    }
    setPhotoMsg('');
    const reader = new FileReader();
    reader.onload = () => openCropForCover(reader.result);
    reader.onerror = () => setPhotoMsg('Could not read that file.');
    reader.readAsDataURL(file);
  }

  // Called by PhotoCropModal with the cropped File (JPEG 92%).
  // Pipeline: upload → write URL to business doc → bump counter → refresh limits.
  async function handleCropDone(croppedFile) {
    const kind = cropState.kind;
    if (!kind || !business?.id || !business?.ownerId) {
      setCropState(s => ({ ...s, open: false }));
      return;
    }
    if (kind === 'logo') setUploadingLogo(true); else setUploadingCover(true);
    try {
      const url = kind === 'logo'
        ? await uploadBusinessLogo(business.id, croppedFile, business.ownerId)
        : await uploadBusinessCover(business.id, croppedFile, business.ownerId);
      // Persist URL to the business doc immediately (silent — doesn't close
      // the overlay). Parent's onPhotoSave does the updateDoc.
      const patch = kind === 'logo'
        ? { logoImageUrl: url }
        : { coverUrl: url };
      await onPhotoSave?.(patch);
      // Bump the monthly counter. Fail-open: if this fails, the upload
      // still succeeded and the URL is saved — counter just won't tick up.
      if (kind === 'logo') {
        await bumpBusinessLogoCount(business.id);
        const refreshed = await checkBusinessLogoLimit(business.id);
        setLogoLimit(refreshed);
      } else {
        await bumpBusinessCoverCount(business.id);
        const refreshed = await checkBusinessCoverLimit(business.id);
        setCoverLimit(refreshed);
      }
      setPhotoMsg('');
    } catch (err) {
      console.error('Photo upload failed:', err);
      setPhotoMsg(err?.message || "Couldn't upload photo. Try again.");
    } finally {
      setCropState(s => ({ ...s, open: false }));
      if (kind === 'logo') setUploadingLogo(false); else setUploadingCover(false);
    }
  }

  // Clearing a photo writes null to the business doc. Storage object is
  // left in place (will be overwritten on next upload at the same path —
  // ~400 KB per business, negligible cost).
  async function handleRemoveLogo() {
    if (uploadingLogo) return;
    try {
      await onPhotoSave?.({ logoImageUrl: null });
      setPhotoMsg('');
    } catch (err) {
      console.error('Remove logo failed:', err);
      setPhotoMsg("Couldn't update — try again.");
    }
  }
  async function handleRemoveCover() {
    if (uploadingCover) return;
    try {
      await onPhotoSave?.({ coverUrl: null });
      setPhotoMsg('');
    } catch (err) {
      console.error('Remove cover failed:', err);
      setPhotoMsg("Couldn't update — try again.");
    }
  }

  const title = mode === 'info' ? 'Business info' : 'Address & delivery';

  function set(field, val) {
    setForm(prev => ({ ...prev, [field]: val }));
  }

  async function handleSubmit() {
    // Build trimmed patch matching the active mode. Empty strings are
    // kept (so the field can be cleared from Firestore on save).
    let patch;
    if (mode === 'info') {
      if (!form.name.trim()) {
        alert('Business name is required');
        return;
      }
      patch = {
        name: form.name.trim(),
        shortDescription: form.shortDescription.trim(),
        description: form.description.trim(),
        category: form.category,
        logo: form.logo,
      };
    } else {
      patch = {
        locality: form.locality.trim(),
        city: form.city.trim(),
        deliveryInstructions: form.deliveryInstructions.trim(),
      };
    }
    setSaving(true);
    try {
      await onSave(patch);
      // Parent closes overlay on success.
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="v2m-overlay-backdrop" onClick={onClose}>
      <div className="v2m-overlay-sheet" onClick={e => e.stopPropagation()}>
        <div className="v2m-overlay-handle" />
        <div className="v2m-overlay-header">
          <div className="v2m-overlay-title">{title}</div>
          <button className="v2m-overlay-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="v2m-overlay-body">
          {mode === 'info' && (
            <>
              {/* ===== Photo error/info banner (Phase 2.3 C2) ===== */}
              {photoMsg && (
                <div className="v2m-photo-banner" role="status">
                  {photoMsg}
                </div>
              )}

              {/* ===== LOGO ===== */}
              <div className="v2m-form-row">
                <label className="v2m-form-label">Logo</label>
                <div className="v2m-logo-edit-row">
                  <div
                    className="v2m-logo-edit-preview"
                    style={{ background: business?.logoImageUrl ? 'transparent' : logoBgFromCategory(form.category) }}
                  >
                    {business?.logoImageUrl ? (
                      <img
                        src={business.logoImageUrl}
                        alt=""
                        className="v2m-logo-edit-img"
                      />
                    ) : (
                      <span className="v2m-logo-edit-emoji">{form.logo}</span>
                    )}
                  </div>
                  <div className="v2m-logo-edit-actions">
                    <button
                      type="button"
                      className="v2m-photo-btn-secondary"
                      onClick={() => logoFileRef.current?.click()}
                      disabled={uploadingLogo || logoLimit.allowed === false}
                    >
                      {uploadingLogo
                        ? 'Uploading…'
                        : (business?.logoImageUrl ? 'Change photo' : 'Upload photo')}
                    </button>
                    {business?.logoImageUrl && (
                      <button
                        type="button"
                        className="v2m-photo-btn-link"
                        onClick={handleRemoveLogo}
                        disabled={uploadingLogo}
                      >
                        Use icon instead
                      </button>
                    )}
                  </div>
                </div>
                <input
                  ref={logoFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleLogoFilePicked}
                />
                {logoLimit.remaining !== null && logoLimit.remaining <= 1 && (
                  <div className="v2m-form-hint v2m-hint-warning">
                    {logoLimit.remaining === 0
                      ? 'No more logo changes this month. You can change it again next month.'
                      : `${logoLimit.remaining} logo change left this month.`}
                  </div>
                )}
                {/* Icon picker is only shown when there's no photo logo set —
                    once a photo is uploaded the emoji is hidden (it would
                    just be confusing alongside the photo). Tapping
                    "Use icon instead" clears the photo and restores this
                    grid. */}
                {!business?.logoImageUrl && (
                  <>
                    <div className="v2m-icon-grid">
                      {LOGO_PRESETS.map(ico => (
                        <button
                          key={ico}
                          type="button"
                          className={`v2m-icon-opt ${form.logo === ico ? 'sel' : ''}`}
                          onClick={() => set('logo', ico)}
                        >
                          {ico}
                        </button>
                      ))}
                    </div>
                    <div className="v2m-form-hint">Pick an icon, or upload a photo above</div>
                  </>
                )}
              </div>

              {/* ===== COVER ===== */}
              <div className="v2m-form-row">
                <label className="v2m-form-label">Cover photo</label>
                <div
                  className="v2m-cover-edit-preview"
                  style={{
                    backgroundImage: business?.coverUrl ? `url("${business.coverUrl}")` : 'none',
                  }}
                >
                  {!business?.coverUrl && (
                    <div className="v2m-cover-edit-placeholder">
                      <ImageIcon size={20} aria-hidden="true" />
                      <span>No cover yet</span>
                    </div>
                  )}
                </div>
                <div className="v2m-cover-edit-actions">
                  <button
                    type="button"
                    className="v2m-photo-btn-secondary"
                    onClick={() => coverFileRef.current?.click()}
                    disabled={uploadingCover || coverLimit.allowed === false}
                  >
                    {uploadingCover
                      ? 'Uploading…'
                      : (business?.coverUrl ? 'Change cover' : 'Upload cover')}
                  </button>
                  {business?.coverUrl && (
                    <button
                      type="button"
                      className="v2m-photo-btn-link"
                      onClick={handleRemoveCover}
                      disabled={uploadingCover}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <input
                  ref={coverFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleCoverFilePicked}
                />
                {coverLimit.remaining !== null && coverLimit.remaining <= 1 && (
                  <div className="v2m-form-hint v2m-hint-warning">
                    {coverLimit.remaining === 0
                      ? 'No more cover changes this month. You can change it again next month.'
                      : `${coverLimit.remaining} cover change left this month.`}
                  </div>
                )}
                <div className="v2m-form-hint">
                  Wide banner shown on your profile. Recommended ratio 16:9.
                </div>
              </div>

              <div className="v2m-form-row">
                <label className="v2m-form-label">Business name</label>
                <input
                  type="text"
                  className="v2m-form-input"
                  placeholder="e.g. Sharma Sweets"
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  maxLength={80}
                />
              </div>

              <div className="v2m-form-row">
                <label className="v2m-form-label">Category</label>
                <select
                  className="v2m-form-input"
                  value={form.category}
                  onChange={e => set('category', e.target.value)}
                >
                  {CATEGORY_OPTIONS.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="v2m-form-row">
                <label className="v2m-form-label">Tagline (one line)</label>
                <input
                  type="text"
                  className="v2m-form-input"
                  placeholder="e.g. Handpicked sarees, woven with love"
                  value={form.shortDescription}
                  onChange={e => set('shortDescription', e.target.value)}
                  maxLength={120}
                />
                <div className="v2m-form-hint">Shown under your name on your profile</div>
              </div>

              <div className="v2m-form-row">
                <label className="v2m-form-label">About</label>
                <textarea
                  className="v2m-form-input"
                  placeholder="Tell customers about your business…"
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  maxLength={1000}
                  rows={4}
                  style={{ resize: 'vertical' }}
                />
              </div>
            </>
          )}

          {mode === 'address' && (
            <>
              <div className="v2m-form-row">
                <label className="v2m-form-label">Locality / area</label>
                <input
                  type="text"
                  className="v2m-form-input"
                  placeholder="e.g. Bandra West"
                  value={form.locality}
                  onChange={e => set('locality', e.target.value)}
                  maxLength={80}
                />
              </div>

              <div className="v2m-form-row">
                <label className="v2m-form-label">City</label>
                <input
                  type="text"
                  className="v2m-form-input"
                  placeholder="e.g. Mumbai"
                  value={form.city}
                  onChange={e => set('city', e.target.value)}
                  maxLength={60}
                />
                <div className="v2m-form-hint">Used to show your business to local customers</div>
              </div>

              <div className="v2m-form-row">
                <label className="v2m-form-label">Delivery instructions</label>
                <textarea
                  className="v2m-form-input"
                  placeholder="e.g. Free delivery within 3 km. ₹40 beyond. Order before 6pm."
                  value={form.deliveryInstructions}
                  onChange={e => set('deliveryInstructions', e.target.value)}
                  maxLength={500}
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
                <div className="v2m-form-hint">Shown to customers on your public profile</div>
              </div>
            </>
          )}
        </div>

        <div className="v2m-overlay-footer">
          <button className="v2m-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="v2m-btn-primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* PhotoCropModal renders as a sibling of the backdrop so it visually
          stacks ABOVE the overlay sheet. Its own z-index in MerchantApp.photos.css
          puts it above v2m-overlay-backdrop. */}
      <PhotoCropModal
        open={cropState.open}
        imageSrc={cropState.imageSrc}
        aspect={cropState.aspect}
        cropShape={cropState.cropShape}
        title={cropState.title}
        onClose={() => setCropState(s => ({ ...s, open: false }))}
        onDone={handleCropDone}
      />
    </div>
  );
}

// =====================================================================
// BUSINESS SWITCHER SHEET (Step 6.0d)
// Bottom sheet listing all businesses owned by the merchant. Tapping a
// row sets it as the active business and closes the sheet. Highlights
// the current selection.
// =====================================================================

function BusinessSwitcherSheet({ businesses, activeId, onPick, onClose }) {
  return (
    <div className="v2m-overlay-backdrop" onClick={onClose}>
      <div className="v2m-overlay-sheet" onClick={e => e.stopPropagation()}>
        <div className="v2m-overlay-handle" />
        <div className="v2m-overlay-header">
          <div className="v2m-overlay-title">Switch business</div>
          <button className="v2m-overlay-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="v2m-overlay-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {businesses.map(b => {
              const isActive = b.id === activeId;
              return (
                <button
                  key={b.id}
                  onClick={() => onPick(b.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 14px',
                    background: isActive ? 'var(--accent-soft, #FBE8D6)' : 'var(--surface, #FFFCF6)',
                    border: `1px solid ${isActive ? 'var(--accent, #C45A3F)' : 'var(--border, #E8DDC6)'}`,
                    borderRadius: 12,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    textAlign: 'left',
                    width: '100%',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <div
                    style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: logoBgFromCategory(b.category),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, flexShrink: 0,
                    }}
                  >
                    {emojiForCategory(b.category)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontFamily: "'Fraunces', Georgia, serif",
                      fontWeight: 600, fontSize: 15,
                      color: 'var(--ink, #2A2418)',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      {b.name}
                      {b.status === 'verified' && (
                        <span style={{
                          width: 14, height: 14, borderRadius: '50%',
                          background: '#3F8E5C', color: '#fff',
                          fontSize: 9, fontWeight: 700,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          lineHeight: 1,
                        }}>✓</span>
                      )}
                    </div>
                    <div style={{
                      fontSize: 11,
                      color: 'var(--ink-soft, #6E6F84)',
                      marginTop: 2,
                    }}>
                      {b.locality || b.city || '—'}
                    </div>
                  </div>
                  {isActive && (
                    <Check size={16} style={{ color: 'var(--accent, #C45A3F)', flexShrink: 0 }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
